import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Child } from '../types';
import { Capacitor } from '@capacitor/core';
import { WidgetBridgePlugin } from 'capacitor-widget-bridge';
import LocalNotificationService, { REMINDER_OPTIONS } from '../services/localNotifications';
import { storage } from '../utils/storage';
import './ChildrenScreen.css';
import { formatDayMonthUK, toLocalDateKey } from '../utils/dateFormat';
import { findLivePlanner, findUpcomingPlanner, plannerStatus, dayOf, UPCOMING_BANNER_DAYS } from '../utils/termPlanner';
import SOCSSetupModal from './SOCSSetupModal';

// Module-level flag so alarm sync only runs once per app session, not per component mount
let __alarmSyncDoneThisSession = false;

// Allow other components to reset the alarm check (e.g. after term planner save creates new activities)
export function resetAlarmSyncFlag() {
  __alarmSyncDoneThisSession = false;
}

// Module-level cache so data survives component remounts (e.g. tab switches)
let __homeDataCache: {
  loadedAt: number;
  dayKey: string;
  userUuid?: string;
  children: any[];
  todayActivities: Record<string, any[]>;
  tomorrowActivities: Record<string, any[]>;
  todayParentActivities: Record<string, any[]>;
  tomorrowParentActivities: Record<string, any[]>;
  childInvitations: Record<string, any[]>;
  childConnections: Record<string, any[]>;
  childSystemNotifications: Record<string, any[]>;
  welcomeNotificationDismissed: boolean;
  holidayPauses: Record<string, any[]>;
} | null = null;
const HOME_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

// Cached data belongs to the person who was signed in when it was loaded, and
// to the calendar day it was loaded on.
//
// Signing in as someone else inside the TTL must not serve — or silently skip
// loading over — the previous account's home screen.
//
// The day check matters because a native WebView is never torn down: backgrounding
// the app on iOS leaves this module alive for days, so without it the elapsed-time
// TTL alone would happily keep serving yesterday's "Today" section across midnight.
function isCacheFor(userUuid?: string) {
  return !!__homeDataCache
    && (Date.now() - __homeDataCache.loadedAt) < HOME_CACHE_TTL_MS
    && __homeDataCache.dayKey === toLocalDateKey()
    && __homeDataCache.userUuid === userUuid;
}

// Invalidate home cache (call after any user action that changes displayed state)
function invalidateHomeCache() {
  __homeDataCache = null;
}

interface ActivityInvitation {
  id: number;
  invitation_uuid?: string;
  activity_id: number;
  activity_uuid?: string;
  activity_name: string;
  activity_description?: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  status: 'pending' | 'accepted' | 'declined';
  host_child_name?: string;
  host_family_name?: string;
  host_parent_name?: string;
  host_parent_email?: string;
  invited_child_uuid?: string;
  invited_child_name?: string;
  message?: string;
  created_at: string;
  website_url?: string;
  activity_type?: string;
  is_tentative?: boolean;
  source?: string; // 'socs', 'pitchero', or undefined for regular activities
}

interface ConnectionRequest {
  id: number;
  request_uuid: string;
  requester_name: string;
  requester_email: string;
  requester_family_name?: string;
  child_name?: string;
  target_child_name?: string;
  message?: string;
  // Set when the request came from a class rep adding the child to their class
  class_uuid?: string;
  class_name?: string;
  created_at: string;
}

interface SystemNotification {
  id: string;
  type: 'system' | 'activity_reminder' | 'connection_rejected' | 'connection_accepted' | 'activity_rejected' | 'activity_accepted' | 'guest_decline' | 'guest_decline_pending' | 'tentative_response' | 'activity_confirmed' | 'activity_cancelled' | 'socs_summary' | 'school_portal_import';
  title: string;
  message: string;
  timestamp: string;
  data?: any;
  child_uuid?: string;
}

interface CalendarActivity {
  activity_uuid: string;
  name: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  venue?: string;
  city?: string;
  contact_email?: string;
  contact_phone?: string;
  passcode?: string;
  website_url?: string;
  activity_type?: string;
  description?: string;
  child_uuid: string;
  child_name: string;
  invited_child_uuid?: string;
  host_parent_name?: string;
  host_parent_email?: string;
  kit_needed?: string[];
  kit_tracking?: Record<string, Record<string, boolean>>;
  reminder_minutes?: number | null;
  responsible_parent_uuid?: string;
}

interface HomeScreenProps {
  onNavigateToChildCalendar?: (child: Child, activityDate?: string) => void;
  onNavigateToChildren?: () => void;
  onNavigateToActivity?: (child: Child, activity: any, calendarState?: any) => void;
  onNavigateToTermPlanner?: (childUuid: string) => void;
  appResumeTrigger?: number;  // Increments when app comes to foreground
}

const HomeScreen: React.FC<HomeScreenProps> = ({ onNavigateToChildCalendar, onNavigateToChildren, onNavigateToActivity, onNavigateToTermPlanner, appResumeTrigger }) => {
  const { user } = useAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const hasFreshCache = isCacheFor(user?.uuid);
  const [loading, setLoading] = useState(!hasFreshCache);
  const [childInvitations, setChildInvitations] = useState<Record<string, ActivityInvitation[]>>({});
  const [childConnections, setChildConnections] = useState<Record<string, ConnectionRequest[]>>({});
  // Class invitations are answered on their own terms — they are not
  // connections, so they arrive from their own endpoint.
  const [childClassInvitations, setChildClassInvitations] = useState<Record<string, any[]>>({});
  // Being asked to help rep a class is addressed to the parent, not a child,
  // so it is kept as a flat list rather than grouped.
  const [repInvitations, setRepInvitations] = useState<any[]>([]);
  // Which of their children is in that class, when they have more than one.
  const [repChildChoice, setRepChildChoice] = useState<Record<string, string>>({});
  const [childSystemNotifications, setChildSystemNotifications] = useState<Record<string, SystemNotification[]>>({});
  const [processingInvitation, setProcessingInvitation] = useState<number | null>(null);
  const [processingConnection, setProcessingConnection] = useState<string | null>(null);
  const [welcomeNotificationDismissed, setWelcomeNotificationDismissed] = useState(
    hasFreshCache ? (__homeDataCache?.welcomeNotificationDismissed ?? false) : true
  );
  const [dismissingInvitation, setDismissingInvitation] = useState<string | null>(null);
  const [showDismissDropdown, setShowDismissDropdown] = useState<string | null>(null);
  const [deletingActivity, setDeletingActivity] = useState<string | null>(null);

  // Designated parent modal state
  const [showDesignatedParentModal, setShowDesignatedParentModal] = useState(false);
  const [selectedParentUuid, setSelectedParentUuid] = useState<string>('');
  const [parents, setParents] = useState<any[]>([]);
  const [pendingInvitationUuid, setPendingInvitationUuid] = useState<string>('');
  const [pendingInvitationAction, setPendingInvitationAction] = useState<'accept' | 'reject'>('accept');

  // Bulk ("all") actions on a child's notifications — a school integration can
  // land dozens at once and answering them one by one is painful.
  const [bulkSnoozeDropdown, setBulkSnoozeDropdown] = useState<string | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState<string | null>(null);
  const [pendingBulkAccept, setPendingBulkAccept] = useState<any[] | null>(null);

  // Ref for notifications section to enable auto-scroll after accepting invitations
  const notificationsSectionRef = useRef<HTMLDivElement>(null);

  // Function to scroll to notifications section
  const scrollToNotifications = () => {
    if (notificationsSectionRef.current) {
      notificationsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Today's and Tomorrow's activities
  const [todayActivities, setTodayActivities] = useState<Record<string, CalendarActivity[]>>({});
  const [tomorrowActivities, setTomorrowActivities] = useState<Record<string, CalendarActivity[]>>({});
  // Parent activities (keyed by participant_parent_uuid)
  const [todayParentActivities, setTodayParentActivities] = useState<Record<string, CalendarActivity[]>>({});
  const [tomorrowParentActivities, setTomorrowParentActivities] = useState<Record<string, CalendarActivity[]>>({});

  // Kit tracking state - same as CalendarScreen
  const [kitTracking, setKitTracking] = useState<Record<string, Record<string, boolean>>>({});

  // Expired term planner state - children whose most recent term planner has ended
  const [expiredTermPlannerChildren, setExpiredTermPlannerChildren] = useState<{childUuid: string; childName: string; termName: string; endDate: string}[]>([]);
  // Planners created for a future term, not yet in force.
  const [upcomingTermPlannerChildren, setUpcomingTermPlannerChildren] = useState<{childUuid: string; childName: string; termName: string; startDate: string}[]>([]);
  // Holiday-return prompt: flipping back to "at school" asks whether the old
  // schedule still applies or a new planner is needed for the new term.
  const [holidayReturnChild, setHolidayReturnChild] = useState<{uuid: string; name: string} | null>(null);
  const [termPlannerBannerDismissed, setTermPlannerBannerDismissed] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('dismissed_term_planner_banners');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  // Children the school portal has moved up a year since their events were
  // imported. The filter is already corrected server-side; the calendar still
  // has to be reloaded to pull the new year's events.
  const [yearGroupChanges, setYearGroupChanges] = useState<Array<{
    childUuid: string;
    childName: string;
    schoolName?: string;
    previousYearGroup?: string | null;
    newYearGroup?: string | null;
  }>>([]);
  const [reloadingCalendarFor, setReloadingCalendarFor] = useState<string | null>(null);

  // SOCS subscriptions pin a child to fixed teams, so they drift: the school
  // adds a sport that can never show up, or the child moves up a year and the
  // team they are on becomes last year's. We cannot work out the right team
  // ourselves - SOCS lists fixtures, not squads - so we prompt and reopen the
  // setup for the parent to amend.
  const [socsReviewPrompts, setSocsReviewPrompts] = useState<Array<{
    promptUuid: string;
    childUuid: string;
    childName: string;
    schoolName?: string;
    reason: string;
    subject: string;
    detail?: string | null;
  }>>([]);
  const [socsReviewChild, setSocsReviewChild] = useState<Child | null>(null);

  // Holiday pause state per child
  const [holidayPauses, setHolidayPauses] = useState<Record<string, any[]>>({});
  const [togglingHoliday, setTogglingHoliday] = useState<string | null>(null);

  // Alarm permission banner state
  const [showAlarmPermissionBanner, setShowAlarmPermissionBanner] = useState(false);
  const [alarmPermissionGranted, setAlarmPermissionGranted] = useState(false);

  // Collapsed state for today/tomorrow sections (keyed by "today-{childUuid}" or "tomorrow-{childUuid}")
  // Initialize from localStorage
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('home_collapsed_sections');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Activity configuration modal state (kit items + designated parent)
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [configureActivity, setConfigureActivity] = useState<CalendarActivity | null>(null);
  const [configureKitItems, setConfigureKitItems] = useState<string[]>([]);
  const [configureNewKitItem, setConfigureNewKitItem] = useState('');
  const [configureParentUuid, setConfigureParentUuid] = useState<string>('');
  const [configureSaving, setConfigureSaving] = useState(false);
  const [showSeriesPrompt, setShowSeriesPrompt] = useState(false);
  const [configureReminderMinutes, setConfigureReminderMinutes] = useState<number | null>(null);
  const [pendingConfigUpdate, setPendingConfigUpdate] = useState<{kit_needed?: string[], responsible_parent_uuid?: string | null} | null>(null);

  const apiService = ApiService.getInstance();

  // Save collapsed sections to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('home_collapsed_sections', JSON.stringify(collapsedSections));
    } catch (e) {
      console.error('Failed to save collapsed sections:', e);
    }
  }, [collapsedSections]);

  // Keep module-level cache in sync with notification/invitation state changes
  useEffect(() => {
    if (__homeDataCache) {
      __homeDataCache.childInvitations = childInvitations;
      __homeDataCache.childConnections = childConnections;
      __homeDataCache.childSystemNotifications = childSystemNotifications;
      __homeDataCache.welcomeNotificationDismissed = welcomeNotificationDismissed;
      __homeDataCache.holidayPauses = holidayPauses;
    }
  }, [childInvitations, childConnections, childSystemNotifications, welcomeNotificationDismissed, holidayPauses]);

  useEffect(() => {
    // Restore from module-level cache if fresh (avoids loading screen on tab switch)
    if (isCacheFor(user?.uuid) && __homeDataCache) {
      console.log('📦 [HomeScreen] Restoring from module cache on mount');
      setChildren(__homeDataCache.children);
      setTodayActivities(__homeDataCache.todayActivities);
      setTomorrowActivities(__homeDataCache.tomorrowActivities);
      setTodayParentActivities(__homeDataCache.todayParentActivities);
      setTomorrowParentActivities(__homeDataCache.tomorrowParentActivities);
      setChildInvitations(__homeDataCache.childInvitations);
      setChildConnections(__homeDataCache.childConnections);
      setChildSystemNotifications(__homeDataCache.childSystemNotifications);
      setWelcomeNotificationDismissed(__homeDataCache.welcomeNotificationDismissed);
      setHolidayPauses(__homeDataCache.holidayPauses || {});
      setLoading(false);
      // Always refresh holiday pauses even from cache — may have changed in profile
      loadHolidayPauses(__homeDataCache.children);
    } else {
      loadChildren(true);
      checkWelcomeNotificationFromBell();
    }
    loadParents();
  }, []);

  // Refresh data when app comes to foreground (uses 2-min cache)
  useEffect(() => {
    if (appResumeTrigger && appResumeTrigger > 0) {
      console.log('📱 [HomeScreen] App resumed - checking cache...');
      loadChildren(); // Skips within the 2-min window, but never across a day change
    }
  }, [appResumeTrigger]);

  // Roll the screen over at midnight.
  //
  // The cache day-guard covers an app that is *resumed* on a new day, but a phone
  // left sitting on the home screen never fires a resume, so "Today" would stay on
  // yesterday until something else touched it. Poll rather than schedule a single
  // timeout to midnight: a suspended WebView does not fire long timers on time, and
  // the poll also catches a device timezone change.
  useEffect(() => {
    const seenDay = { current: toLocalDateKey() };
    const timer = setInterval(() => {
      const dayNow = toLocalDateKey();
      if (dayNow !== seenDay.current) {
        console.log(`🌅 [HomeScreen] Day rolled over to ${dayNow} - refreshing`);
        seenDay.current = dayNow;
        invalidateHomeCache();
        loadChildren(true);
      }
    }, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-scroll to notifications section when returning from calendar/activity view
  useEffect(() => {
    const shouldScroll = sessionStorage.getItem('scrollToNotificationsOnReturn');
    if (shouldScroll === 'true') {
      // Clear the flag
      sessionStorage.removeItem('scrollToNotificationsOnReturn');
      // Scroll after a short delay to ensure content is loaded
      setTimeout(() => scrollToNotifications(), 500);
    }
  }, []);

  // Retry any pending notification/connection responses from localStorage on mount
  useEffect(() => {
    const retryPendingResponses = async () => {
      // Retry pending invitation responses
      const pendingInvitations = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
      for (const pending of pendingInvitations) {
        console.log('🔄 Retrying pending invitation response:', pending);
        processInvitationResponseAsync(pending.invitationUuid, pending.action, pending.invitation);
      }

      // Retry pending connection responses
      const pendingConnections = JSON.parse(localStorage.getItem('pendingConnectionResponses') || '[]');
      for (const pending of pendingConnections) {
        console.log('🔄 Retrying pending connection response:', pending);
        processConnectionResponseAsync(pending.connectionUuid, pending.action);
      }

      // Retry pending invitation dismissals (clean up entries older than 7 days)
      const pendingDismissals = JSON.parse(localStorage.getItem('pendingInvitationDismissals') || '[]');
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const activeDismissals = pendingDismissals.filter((d: any) => !d.timestamp || d.timestamp > sevenDaysAgo);
      if (activeDismissals.length < pendingDismissals.length) {
        console.log(`🧹 Cleaned up ${pendingDismissals.length - activeDismissals.length} stale pending dismissals`);
        localStorage.setItem('pendingInvitationDismissals', JSON.stringify(activeDismissals));
      }
      for (const pending of activeDismissals) {
        if (pending.notificationId) {
          console.log('🔄 Retrying pending snooze:', pending.notificationId);
          processSnoozeAsync(pending.notificationId, pending.reminderPeriod, 0);
        }
      }
    };

    retryPendingResponses();
  }, []);

  // Check AlarmKit permission on native iOS - show banner if user has upcoming activities and AlarmKit not granted
  const alarmCheckDone = useRef(__alarmSyncDoneThisSession);
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || alarmCheckDone.current) return;
    if (children.length === 0) return; // Wait until children are loaded (auth ready)

    alarmCheckDone.current = true;
    __alarmSyncDoneThisSession = true;
    const checkAlarmPerm = async () => {
      try {
        const localNotifService = LocalNotificationService.getInstance();
        const { available, granted } = await localNotifService.checkAlarmPermission();
        if (!available) return;
        if (granted) {
          setAlarmPermissionGranted(true);
          // Permission already granted - sync alarms (fire-and-forget)
          (async () => {
            try {
              // Check for background promotion log from native side and clear it
              const promotionLog = await storage.getItem('alarm_promotion_log');
              if (promotionLog) {
                await storage.removeItem('alarm_promotion_log');
              }

              // One-time purge - v10 switches to .timer(duration:) for alarms within 24h
              const purgeKey = 'alarm_purge_v10_done';
              const purgeDone = localStorage.getItem(purgeKey);
              if (!purgeDone) {
                console.log('🧹 One-time purge of stale alarms...');
                await localNotifService.purgeAllAlarms();
                localStorage.setItem(purgeKey, Date.now().toString());
              }

              const now = new Date();
              const farFuture = new Date(now);
              farFuture.setFullYear(farFuture.getFullYear() + 1);
              const calResponse = await apiService.getCalendarActivities(
                now.toISOString().split('T')[0],
                farFuture.toISOString().split('T')[0]
              );
              if (calResponse.success && calResponse.data) {
                const upcoming = Array.isArray(calResponse.data) ? calResponse.data : [];
                // Sort by date ascending so nearest events get AlarmKit priority (limited slots)
                upcoming.sort((a, b) => {
                  const dateA = new Date(`${(a.start_date || '').split('T')[0]}T${a.start_time || '00:00'}`);
                  const dateB = new Date(`${(b.start_date || '').split('T')[0]}T${b.start_time || '00:00'}`);
                  return dateA.getTime() - dateB.getTime();
                });
                let scheduled = 0;
                for (const a of upcoming) {
                  if (a.reminder_minutes != null && a.reminder_minutes >= 0) {
                    const actUuid = a.activity_uuid || a.uuid;
                    const existing = await localNotifService.getReminder(actUuid);
                    // Skip if already scheduled with same minutes AND already using AlarmKit
                    // Re-schedule if it was a local notification so it upgrades to AlarmKit
                    if (existing && existing.minutesBefore === a.reminder_minutes && existing.useAlarmKit) {
                      continue;
                    }
                    // Cancel old reminder before re-scheduling (upgrade or minutes change)
                    if (existing) {
                      await localNotifService.cancelReminder(actUuid);
                    }
                    const result = await localNotifService.scheduleReminder({
                      activityUuid: actUuid,
                      activityName: a.name,
                      startDate: a.start_date,
                      startTime: a.start_time || '00:00',
                      endTime: a.end_time || '',
                      minutesBefore: a.reminder_minutes,
                      reminderTarget: a.reminder_target || 'start',
                      childName: a.child_name || a.childName || '',
                    });
                    if (result) {
                      scheduled++;
                    }
                  }
                }
              }
            } catch (e) {
              console.log('Failed to sync alarms on load', e);
            }
          })();
          return;
        }

        // Check 30 days of upcoming activities for any with reminders
        const now = new Date();
        const thirtyDaysOut = new Date(now);
        thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
        const response = await apiService.getCalendarActivities(
          now.toISOString().split('T')[0],
          thirtyDaysOut.toISOString().split('T')[0]
        );
        if (response.success && response.data) {
          const activities = Array.isArray(response.data) ? response.data : [];
          const hasReminders = activities.some((a: any) => a.reminder_minutes != null && a.reminder_minutes >= 0);
          if (hasReminders) {
            setShowAlarmPermissionBanner(true);
          }
        }
      } catch (e) {
        // AlarmKit not available on this device
      }
    };
    checkAlarmPerm();
  }, [children]);

  // Auto-sync School Portal events weekly
  useEffect(() => {
    const autoSyncSchoolPortal = async () => {
      try {
        console.log('🔄 [AUTO-SYNC] Checking for School Portal updates...');
        const response = await apiService.schoolPortalAutoSync();

        if (response.success && response.data) {
          const { syncPerformed, totalNewActivities, message } = response.data;

          if (syncPerformed && totalNewActivities > 0) {
            console.log(`✅ [AUTO-SYNC] ${message}`);
            // Could show a subtle toast notification here if desired
            // toast.success(message);
          } else {
            console.log(`ℹ️ [AUTO-SYNC] ${message || 'No updates needed'}`);
          }
        }
      } catch (error) {
        // Silently fail - don't bother user if auto-sync fails
        console.log('ℹ️ [AUTO-SYNC] Skipped - No School Portal configured or sync failed');
      }
    };

    const loadYearGroupChanges = async () => {
      try {
        const response = await apiService.schoolPortalYearGroupChanges();
        if (response.success && Array.isArray(response.data)) {
          setYearGroupChanges(response.data);
        }
      } catch {
        // Non-critical - the prompt just won't show this visit.
      }
    };

    const loadSocsReviewPrompts = async () => {
      try {
        const response = await apiService.socsReviewPrompts();
        if (response.success && Array.isArray(response.data)) {
          setSocsReviewPrompts(response.data);
        }
      } catch {
        // Non-critical - the prompt just won't show this visit.
      }
    };

    // Check once now for anything flagged by an earlier sync, then again after
    // the sync above has had time to run: it hands off to a background job, so
    // a year change it detects lands after the request has already returned.
    autoSyncSchoolPortal().then(() => {
      loadYearGroupChanges();
      loadSocsReviewPrompts();
      setTimeout(() => {
        loadYearGroupChanges();
        loadSocsReviewPrompts();
      }, 20000);
    });
  }, []);

  /**
   * Some sites store their portal URL as the school_name, which reads badly in
   * a sentence. Turn a URL into its school subdomain ("westbrookhay"), and fall
   * back to a generic phrase when there's nothing readable.
   */
  const readableSchoolName = (name?: string): string => {
    if (!name) return 'The school portal';
    if (!/^https?:\/\//i.test(name)) return name;
    try {
      const host = new URL(name).hostname.replace(/^www\./, '');
      const label = host.split('.')[0];
      return label ? label.charAt(0).toUpperCase() + label.slice(1) : 'The school portal';
    } catch {
      return 'The school portal';
    }
  };

  /**
   * SOCS stores a school as the bare slug used in its URL ("st albans"), which
   * readableSchoolName leaves alone because it only tidies real URLs.
   */
  const socsSchoolLabel = (name?: string): string => {
    if (!name) return 'Your school';
    return name.replace(/\b[a-z]/g, ch => ch.toUpperCase());
  };

  /** Reload a child's school-portal calendar so the new year's events import. */
  const handleReloadSchoolPortalCalendar = async (childUuid: string) => {
    setReloadingCalendarFor(childUuid);
    try {
      const response = await apiService.schoolPortalAutoSync(true);
      if (response.success) {
        await apiService.schoolPortalAcknowledgeYearGroupChange(childUuid);
        setYearGroupChanges(prev => prev.filter(c => c.childUuid !== childUuid));
      } else {
        alert('Could not reload the calendar just now. Please try again shortly.');
      }
    } catch {
      alert('Could not reload the calendar just now. Please try again shortly.');
    } finally {
      setReloadingCalendarFor(null);
    }
  };

  /** Dismiss the prompt without reloading. A later year change raises a new one. */
  /** Reopen SOCS setup on the flagged child so the parent can fix it there. */
  const handleAmendSocsSetup = (childUuid: string) => {
    const child = children.find(c => c.uuid === childUuid);
    if (child) setSocsReviewChild(child);
  };

  /**
   * Closing the setup clears that child's prompts only if they actually saved -
   * backing out leaves the banner up, since nothing has been amended yet.
   */
  const handleSocsSetupClosed = async (completed: boolean) => {
    const childUuid = socsReviewChild?.uuid;
    setSocsReviewChild(null);
    if (!completed || !childUuid) return;
    setSocsReviewPrompts(prev => prev.filter(p => p.childUuid !== childUuid));
    try {
      await apiService.socsAcknowledgeReviewPrompt({ childUuid });
    } catch {
      // Hidden locally regardless; it reappears next load if this failed.
    }
  };

  const handleDismissSocsPrompt = async (promptUuid: string) => {
    setSocsReviewPrompts(prev => prev.filter(p => p.promptUuid !== promptUuid));
    try {
      await apiService.socsAcknowledgeReviewPrompt({ promptUuid });
    } catch {
      // Hidden locally regardless; it reappears next load if this failed.
    }
  };

  const handleDismissYearGroupChange = async (childUuid: string) => {
    setYearGroupChanges(prev => prev.filter(c => c.childUuid !== childUuid));
    try {
      await apiService.schoolPortalAcknowledgeYearGroupChange(childUuid);
    } catch {
      // Hidden locally regardless; it will reappear next load if this failed.
    }
  };

  const loadParents = async () => {
    try {
      const response = await apiService.getParents();
      if (response.success && response.data) {
        setParents(response.data);
        // Auto-select current user as default if available
        if (user && response.data.length > 0) {
          const currentParent = response.data.find((p: any) => p.uuid === user.uuid);
          if (currentParent) {
            setSelectedParentUuid(currentParent.uuid);
          } else if (response.data.length > 0) {
            setSelectedParentUuid(response.data[0].uuid);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load parents:', error);
    }
  };

  const handleDismissWelcomeNotification = async () => {
    try {
      // Call the dismiss API to remove from database
      await apiService.dismissNotification('welcome_notification', 'welcome');
      console.log('✅ Welcome notification dismissed via API');

      // Update local state
      setWelcomeNotificationDismissed(true);
    } catch (error) {
      console.error('Failed to dismiss welcome notification:', error);
      // Still update UI even if API call fails
      setWelcomeNotificationDismissed(true);
    }
  };

  const checkWelcomeNotificationFromBell = async () => {
    try {
      // Check bell API to see if welcome notification exists
      const bellResponse = await apiService.getBellNotifications();

      if (bellResponse.success && bellResponse.data) {
        // Look for welcome notification in bell data
        const welcomeNotification = bellResponse.data.find(
          (notification: any) => notification.type === 'welcome'
        );

        console.log('🔔 Welcome notification check:', {
          hasWelcomeNotification: !!welcomeNotification,
          totalNotifications: bellResponse.data.length
        });

        // If welcome notification exists in bell, show it. If not, don't show it (was dismissed)
        setWelcomeNotificationDismissed(!welcomeNotification);
      } else {
        // No notifications at all, don't show welcome
        setWelcomeNotificationDismissed(true);
      }
    } catch (error) {
      console.error('Failed to check welcome notification from bell:', error);
      setWelcomeNotificationDismissed(true);
    }
  };

  const loadChildren = async (forceRefresh: boolean = false) => {
    // Skip reload if data was loaded recently (2-min cache) unless forced
    const now = Date.now();
    if (!forceRefresh && isCacheFor(user?.uuid)) {
      console.log(`📦 [HomeScreen] Using cached data (${Math.round((now - (__homeDataCache?.loadedAt || now)) / 1000)}s old)`);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getChildren();

      if (response.success && response.data) {
        const childrenData = Array.isArray(response.data)
          ? response.data
          : (response.data as any)?.data || [];

        setChildren(childrenData);

        // Load notifications
        await loadInvitationsFromBell(childrenData);
        await loadConnectionRequestsForChildren(childrenData);
        await loadClassInvitationsForChildren(childrenData);
        await loadRepInvitations();
        await loadSystemNotificationsForChildren(childrenData);

        // Load today's and tomorrow's activities
        await loadTodayAndTomorrowActivities(childrenData);

        // Check for expired term planners (non-blocking)
        checkExpiredTermPlanners(childrenData);

        // Load holiday pauses for all children (non-blocking)
        loadHolidayPauses(childrenData);
      } else {
        console.error('Failed to load children:', response.error);
        setChildren([]);
      }
    } catch (error) {
      console.error('Load children error:', error);
      setChildren([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if any child has an expired term planner (most recent one ended)
  const checkExpiredTermPlanners = async (childrenData: Child[]) => {
    try {
      const primaryParent = parents.length > 0 ? (parents.find((p: any) => p.is_primary) || parents[0]) : null;
      if (!primaryParent) {
        // Parents not loaded yet - try fetching
        const parentsResponse = await apiService.getParents();
        if (!parentsResponse.success || !parentsResponse.data || parentsResponse.data.length === 0) return;
        var parentUuid = (parentsResponse.data.find((p: any) => p.is_primary) || parentsResponse.data[0]).uuid;
      } else {
        var parentUuid = primaryParent.uuid;
      }

      const response = await apiService.getTermPlanners(parentUuid);
      if (!response.success || !response.data || response.data.length === 0) return;

      const expired: {childUuid: string; childName: string; termName: string; endDate: string}[] = [];
      const upcomingList: {childUuid: string; childName: string; termName: string; startDate: string}[] = [];

      // Group term planners by child and find the most recent one
      const plannersByChild: Record<string, any[]> = {};
      for (const planner of response.data) {
        const childUuid = planner.child_uuid;
        if (!plannersByChild[childUuid]) plannersByChild[childUuid] = [];
        plannersByChild[childUuid].push(planner);
      }

      for (const [childUuid, planners] of Object.entries(plannersByChild)) {
        const child = childrenData.find(c => c.uuid === childUuid);
        if (!child) continue;
        const childName = child.name || `${child.first_name} ${child.last_name}`;

        // A planner queued for a future term. Informational only — today's
        // schedule is unaffected until it takes over.
        const upcoming = findUpcomingPlanner(planners, undefined, UPCOMING_BANNER_DAYS);
        if (upcoming) {
          upcomingList.push({
            childUuid,
            childName,
            termName: upcoming.term_name || '',
            startDate: dayOf(upcoming.start_date)
          });
        }

        // Never nag while a planner is in force. A child can hold an old dated
        // planner alongside a live one; telling the parent their planner "has
        // ended" while today's schedule is being generated is simply wrong.
        if (findLivePlanner(childUuid, planners)) continue;

        // Nothing live: warn about the most recent planner that has run out.
        // Open-ended planners cannot expire, so they are not candidates.
        const lapsed = planners
          .filter((p: any) => plannerStatus(p) === 'finished' && dayOf(p.end_date))
          .sort((a: any, b: any) => (dayOf(a.end_date) < dayOf(b.end_date) ? 1 : -1))[0];

        if (lapsed) {
          expired.push({
            childUuid,
            childName,
            termName: lapsed.term_name,
            endDate: dayOf(lapsed.end_date)
          });
        }
      }

      setExpiredTermPlannerChildren(expired);
      setUpcomingTermPlannerChildren(upcomingList);
    } catch (error) {
      console.error('Failed to check expired term planners:', error);
    }
  };

  const loadHolidayPauses = async (childrenData: Child[]) => {
    try {
      const pausesByChild: Record<string, any[]> = {};
      for (const child of childrenData) {
        const response = await apiService.getHolidayPauses(child.uuid);
        if (response.success && response.data) {
          const today = new Date().toISOString().split('T')[0];
          const activePauses = (response.data as any[]).filter((p: any) => {
            const start = (p.pause_start || '').split('T')[0];
            const end = p.pause_end ? (p.pause_end || '').split('T')[0] : '2099-12-31';
            return start <= today && end >= today;
          });
          if (activePauses.length > 0) {
            pausesByChild[child.uuid] = activePauses;
          }
        }
      }
      setHolidayPauses(pausesByChild);
    } catch (error) {
      console.error('Failed to load holiday pauses:', error);
    }
  };

  const toggleHolidayMode = async (childUuid: string, childName: string) => {
    setTogglingHoliday(childUuid);
    try {
      const existingPauses = holidayPauses[childUuid] || [];
      if (existingPauses.length > 0) {
        for (const pause of existingPauses) {
          await apiService.deleteHolidayPause(childUuid, pause.uuid);
        }
        setHolidayPauses(prev => {
          const updated = { ...prev };
          delete updated[childUuid];
          return updated;
        });
        await loadChildren(true);
      } else {
        const response = await apiService.createHolidayPause(childUuid, {
          label: 'Holiday'
        });
        if (response.success && response.data) {
          setHolidayPauses(prev => ({
            ...prev,
            [childUuid]: [response.data]
          }));

          if (Capacitor.isNativePlatform()) {
            const localNotifService = LocalNotificationService.getInstance();
            const childActivityUuids = [
              ...(todayActivities[childUuid] || []),
              ...(tomorrowActivities[childUuid] || [])
            ].map(a => a.activity_uuid || (a as any).uuid).filter(Boolean);
            for (const actUuid of childActivityUuids) {
              await localNotifService.cancelReminder(actUuid);
            }
          }

          await loadChildren(true);
        }
      }
    } catch (error) {
      console.error('Failed to toggle holiday mode:', error);
      alert('Failed to toggle holiday mode. Please try again.');
    } finally {
      setTogglingHoliday(null);
    }
  };

  const loadTodayAndTomorrowActivities = async (childrenData: Child[]) => {
    try {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Local, not UTC: toISOString() would ask for yesterday's activities for
      // the first hour of every BST day.
      const todayStr = toLocalDateKey(today);
      const tomorrowStr = toLocalDateKey(tomorrow);

      // Load activities for today and tomorrow
      const todayResponse = await apiService.getCalendarActivities(todayStr, todayStr);
      const tomorrowResponse = await apiService.getCalendarActivities(tomorrowStr, tomorrowStr);

      const todayByChild: Record<string, CalendarActivity[]> = {};
      const tomorrowByChild: Record<string, CalendarActivity[]> = {};
      const todayByParent: Record<string, CalendarActivity[]> = {};
      const tomorrowByParent: Record<string, CalendarActivity[]> = {};

      // Initialize empty arrays for each child
      childrenData.forEach(child => {
        todayByChild[child.uuid] = [];
        tomorrowByChild[child.uuid] = [];
      });

      // Process today's activities
      if (todayResponse.success && todayResponse.data) {
        const activities = Array.isArray(todayResponse.data) ? todayResponse.data : [];
        activities.forEach((activity: any) => {
          // Check if this is a parent activity (has participant_parent_uuid but no child_uuid)
          if (activity.participant_parent_uuid && !activity.child_uuid) {
            const parentUuid = activity.participant_parent_uuid;
            if (!todayByParent[parentUuid]) {
              todayByParent[parentUuid] = [];
            }
            todayByParent[parentUuid].push(activity);
          } else {
            // Regular child activity
            const childUuid = activity.child_uuid || activity.invited_child_uuid;
            if (childUuid && todayByChild[childUuid]) {
              todayByChild[childUuid].push(activity);
            }
          }
        });
      }

      // Process tomorrow's activities
      if (tomorrowResponse.success && tomorrowResponse.data) {
        const activities = Array.isArray(tomorrowResponse.data) ? tomorrowResponse.data : [];
        activities.forEach((activity: any) => {
          // Check if this is a parent activity (has participant_parent_uuid but no child_uuid)
          if (activity.participant_parent_uuid && !activity.child_uuid) {
            const parentUuid = activity.participant_parent_uuid;
            if (!tomorrowByParent[parentUuid]) {
              tomorrowByParent[parentUuid] = [];
            }
            tomorrowByParent[parentUuid].push(activity);
          } else {
            // Regular child activity
            const childUuid = activity.child_uuid || activity.invited_child_uuid;
            if (childUuid && tomorrowByChild[childUuid]) {
              tomorrowByChild[childUuid].push(activity);
            }
          }
        });
      }

      setTodayActivities(todayByChild);
      setTomorrowActivities(tomorrowByChild);
      setTodayParentActivities(todayByParent);
      setTomorrowParentActivities(tomorrowByParent);

      // Save to module-level cache (survives component remounts on tab switch)
      // Note: notification states are snapshotted separately via updateHomeCache()
      __homeDataCache = {
        loadedAt: Date.now(),
        dayKey: todayStr,
        userUuid: user?.uuid,
        children: childrenData,
        todayActivities: todayByChild,
        tomorrowActivities: tomorrowByChild,
        todayParentActivities: todayByParent,
        tomorrowParentActivities: tomorrowByParent,
        childInvitations: {},
        childConnections: {},
        childSystemNotifications: {},
        welcomeNotificationDismissed: welcomeNotificationDismissed,
        holidayPauses: holidayPauses,
      };

      // Update iOS home screen widget with today's and tomorrow's activities
      updateHomeScreenWidget(childrenData, todayByChild, tomorrowByChild, todayByParent, tomorrowByParent);
    } catch (error) {
      console.error('Failed to load today/tomorrow activities:', error);
    }
  };

  // Update iOS home screen widget with today's and tomorrow's activity data
  const updateHomeScreenWidget = async (
    childrenData: Child[],
    todayByChild: Record<string, CalendarActivity[]>,
    tomorrowByChild: Record<string, CalendarActivity[]>,
    todayByParent: Record<string, CalendarActivity[]> = {},
    tomorrowByParent: Record<string, CalendarActivity[]> = {}
  ) => {
    try {
      // Only run on iOS native app
      if (Capacitor.getPlatform() !== 'ios') return;

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayStr = today.toISOString().split('T')[0];
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      const mapActivities = (activities: CalendarActivity[]) => activities.map(activity => {
        const actName = (activity.name || '').toLowerCase();
        const isPickUp = actName.includes('pick up') || actName.includes('pickup');
        return {
          startTime: (isPickUp && activity.end_time) ? activity.end_time : (activity.start_time || '00:00'),
          name: activity.name || 'Activity'
        };
      });

      // Build child entries
      const childEntries = childrenData.map(child => ({
        uuid: child.uuid,
        name: child.first_name || child.name?.split(' ')[0] || 'Child',
        activities: mapActivities(todayByChild[child.uuid] || [])
      }));

      // Build parent entries
      const parentEntries = Object.entries(todayByParent).map(([parentUuid, activities]) => ({
        uuid: parentUuid,
        name: (activities[0] as any)?.participant_parent_name?.split(' ')[0] || 'Parent',
        activities: mapActivities(activities)
      }));

      const childTomorrowEntries = childrenData.map(child => ({
        uuid: child.uuid,
        name: child.first_name || child.name?.split(' ')[0] || 'Child',
        activities: mapActivities(tomorrowByChild[child.uuid] || [])
      }));

      const parentTomorrowEntries = Object.entries(tomorrowByParent).map(([parentUuid, activities]) => ({
        uuid: parentUuid,
        name: (activities[0] as any)?.participant_parent_name?.split(' ')[0] || 'Parent',
        activities: mapActivities(activities)
      }));

      // Build widget data structure with activity names and times for today and tomorrow
      const widgetData = {
        lastUpdated: new Date().toISOString(),
        date: todayStr,
        children: [...childEntries, ...parentEntries],
        tomorrowDate: tomorrowStr,
        tomorrowChildren: [...childTomorrowEntries, ...parentTomorrowEntries]
      };

      // Store data in App Group for widget to read
      await WidgetBridgePlugin.setItem({
        key: 'todayActivities',
        value: JSON.stringify(widgetData),
        group: 'group.com.cluubz.app'
      });

      // Tell widgets to refresh their timelines
      await WidgetBridgePlugin.reloadAllTimelines();

      console.log('✅ [Widget] Updated home screen widget with today\'s and tomorrow\'s activities');
    } catch (error) {
      // Silently fail - widget update is not critical
      console.log('ℹ️ [Widget] Could not update widget:', error);
    }
  };

  const loadInvitationsFromBell = async (childrenData: Child[]) => {
    try {
      const response = await apiService.getBellNotifications();

      if (response.success && response.data) {
        const invitationsByChild: Record<string, ActivityInvitation[]> = {};

        // Initialize empty arrays for each child
        childrenData.forEach(child => {
          invitationsByChild[child.uuid] = [];
        });

        // Check for welcome notification in the same bell data (avoids separate API call)
        const welcomeNotification = response.data.find(
          (notification: any) => notification.type === 'welcome'
        );
        setWelcomeNotificationDismissed(!welcomeNotification);

        // Process notifications - backend already filters by current user
        response.data.forEach((notification: any) => {
          if (notification.type === 'activity_invitation' && notification.data) {
            const invitationData = notification.data;

            // Extract activity name from data or fallback to parsing the message
            let activityName = invitationData.activityName;
            if (!activityName && notification.message) {
              const match = notification.message.match(/invited you to (.+)$/);
              if (match) {
                activityName = match[1].replace(/^["']|["']$/g, '');
              }
            }

            // Show this notification only to the specific child it targets
            const targetChildUuid = notification.child_uuid;

            if (targetChildUuid && invitationsByChild[targetChildUuid]) {
              // Debug logging for invitation data
              console.log('🔍 HOME: Processing notification for invitation:', {
                notification_id: notification.id,
                notification_type: notification.type,
                invitationData_is_tentative: invitationData.is_tentative,
                invitationData_full: invitationData,
                activityName: activityName
              });

              // Check if guest has responded "not_interested"
              const hasNotInterestedResponse = invitationData.tentative_response === 'not_interested';

              const formattedInvitation: ActivityInvitation = {
                id: `notification-${notification.id}`,
                invitation_uuid: invitationData.invitationUuid || '',
                activity_id: 0,
                activity_uuid: invitationData.activity_uuid || invitationData.activityUuid || '',
                activity_name: activityName || 'Unknown Activity',
                activity_description: invitationData.isRecurringSeries ? notification.message : `Activity invitation`,
                start_date: invitationData.startDate || invitationData.start_date || new Date().toISOString().split('T')[0],
                end_date: invitationData.endDate || invitationData.end_date || null,
                start_time: invitationData.startTime || invitationData.start_time || null,
                end_time: invitationData.endTime || invitationData.end_time || null,
                location: null,
                status: hasNotInterestedResponse ? 'declined' : 'pending',
                host_child_name: invitationData.hostChildName || '',
                host_family_name: '',
                host_parent_name: '',
                host_parent_email: '',
                invited_child_uuid: targetChildUuid,
                invited_child_name: notification.child_name || '',
                message: null,
                invitation_message: notification.message,
                created_at: notification.timestamp,
                website_url: null,
                activity_type: null,
                is_tentative: invitationData.is_tentative || false,
                tentative_response: invitationData.tentative_response,
                isRecurringSeries: invitationData.isRecurringSeries || false,
                seriesId: invitationData.seriesId,
                sessionCount: invitationData.sessionCount,
                dateRange: invitationData.dateRange,
                dismissible: (notification as any).dismissible !== false,
                source: invitationData.source, // 'socs', 'pitchero', or undefined
                notification_id: notification.id // Raw notification_id from DB for dismiss matching
              } as any;

              console.log('🔍 HOME: Created formatted invitation with is_tentative:', formattedInvitation.is_tentative);

              invitationsByChild[targetChildUuid].push(formattedInvitation);
            }
          }
        });

        // Filter out any invitations that are pending local dismissal (snooze)
        const pendingDismissals = JSON.parse(localStorage.getItem('pendingInvitationDismissals') || '[]');
        const dismissedNotifIds = new Set(pendingDismissals.map((d: any) => d.notificationId).filter(Boolean));
        if (dismissedNotifIds.size > 0) {
          Object.keys(invitationsByChild).forEach(childId => {
            invitationsByChild[childId] = invitationsByChild[childId].filter(
              inv => !dismissedNotifIds.has((inv as any).notification_id)
            );
          });
        }

        setChildInvitations(invitationsByChild);
      } else {
        const invitationsByChild: Record<string, ActivityInvitation[]> = {};
        childrenData.forEach(child => {
          invitationsByChild[child.uuid] = [];
        });
        setChildInvitations(invitationsByChild);
      }
    } catch (error) {
      console.error('Failed to load invitations from bell notifications:', error);
      const invitationsByChild: Record<string, ActivityInvitation[]> = {};
      childrenData.forEach(child => {
        invitationsByChild[child.uuid] = [];
      });
      setChildInvitations(invitationsByChild);
    }
  };

  const loadConnectionRequestsForChildren = async (childrenData: Child[]) => {
    try {
      const connectionsByChild: Record<string, ConnectionRequest[]> = {};

      // Initialize empty arrays for each child
      childrenData.forEach(child => {
        connectionsByChild[child.uuid] = [];
      });

      // Fetch ALL pending connection requests for the current user
      try {
        const connectionsResponse = await apiService.getConnectionRequests();

        if (connectionsResponse.success && connectionsResponse.data) {
          // Group ALL pending connection requests by target child UUID
          connectionsResponse.data.forEach((request: any) => {
            if (request.status === 'pending') {
              const targetChildUuid = request.target_child_uuid;
              if (targetChildUuid && connectionsByChild[targetChildUuid]) {
                connectionsByChild[targetChildUuid].push({
                  id: request.id,
                  request_uuid: request.request_uuid,
                  requester_name: request.requester_name,
                  requester_email: request.requester_email,
                  requester_family_name: request.requester_family_name,
                  child_name: request.child_name,
                  target_child_name: request.target_child_name,
                  class_uuid: request.class_uuid,
                  class_name: request.class_name,
                  message: request.message,
                  created_at: request.created_at
                });
              }
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch connection request data:', error);
      }

      setChildConnections(connectionsByChild);
    } catch (error) {
      console.error('Failed to load connection requests:', error);
      const connectionsByChild: Record<string, ConnectionRequest[]> = {};
      childrenData.forEach(child => {
        connectionsByChild[child.uuid] = [];
      });
      setChildConnections(connectionsByChild);
    }
  };

  const loadClassInvitationsForChildren = async (childrenData: Child[]) => {
    try {
      const response = await apiService.getClassInvitations();
      const byChild: Record<string, any[]> = {};
      childrenData.forEach(child => { byChild[child.uuid] = []; });

      if (response.success && response.data) {
        response.data.forEach((invitation: any) => {
          if (byChild[invitation.child_uuid]) {
            byChild[invitation.child_uuid].push(invitation);
          }
        });
      }
      setChildClassInvitations(byChild);
    } catch (error) {
      console.error('Failed to load class invitations:', error);
      setChildClassInvitations({});
    }
  };

  const loadRepInvitations = async () => {
    try {
      const response = await apiService.getClassRepInvitations();
      setRepInvitations(response.success && response.data ? response.data : []);
    } catch (error) {
      console.error('Failed to load rep invitations:', error);
      setRepInvitations([]);
    }
  };

  const handleRepInvitationResponse = async (repUuid: string, action: 'accept' | 'decline') => {
    const childUuid = repChildChoice[repUuid];
    setRepInvitations(prev => prev.filter(r => r.rep_uuid !== repUuid));
    try {
      const response = await apiService.respondToClassRepInvitation(repUuid, action, childUuid);
      if (!response.success) {
        console.error('Failed to respond to rep invitation:', response.error);
      }
    } catch (error) {
      console.error('Failed to respond to rep invitation:', error);
    }
  };

  const handleClassInvitationResponse = async (memberUuid: string, action: 'accept' | 'decline') => {
    // Drop it from the list straight away; the request runs behind that.
    setChildClassInvitations(prev => {
      const next: Record<string, any[]> = {};
      Object.entries(prev).forEach(([childUuid, list]) => {
        next[childUuid] = list.filter((i: any) => i.member_uuid !== memberUuid);
      });
      return next;
    });

    try {
      const response = await apiService.respondToClassInvitation(memberUuid, action);
      if (!response.success) {
        console.error('Failed to respond to class invitation:', response.error);
        return;
      }

      // Accepting a class hands over the activities it has already been sent,
      // so the lists have to be re-read — otherwise they only appear on the
      // next full page load.
      if (children.length > 0) {
        await Promise.all([
          loadInvitationsFromBell(children),
          loadSystemNotificationsForChildren(children),
          loadClassInvitationsForChildren(children)
        ]);
      }
      window.dispatchEvent(new CustomEvent('notification-refresh'));
      // Accepting a class can hand over activities, which land on the
      // calendar — it listens for this, and hears nothing otherwise.
      window.dispatchEvent(new CustomEvent('activityInvitationResponded', {
        detail: { action, source: 'class_invitation' }
      }));
    } catch (error) {
      console.error('Failed to respond to class invitation:', error);
    }
  };

  const loadSystemNotificationsForChildren = async (childrenData: Child[]) => {
    try {
      const response = await apiService.getBellNotifications();

      if (response.success && response.data) {
        const systemNotificationsByChild: Record<string, SystemNotification[]> = {};

        // Initialize empty arrays for each child
        childrenData.forEach(child => {
          systemNotificationsByChild[child.uuid] = [];
        });

        // Process notifications and extract system messages, activity reminders, and response notifications
        response.data.forEach((notification: any) => {
          if (notification.type === 'system' ||
              notification.type === 'activity_reminder' ||
              notification.type === 'connection_rejected' ||
              notification.type === 'connection_accepted' ||
              notification.type === 'activity_rejected' ||
              notification.type === 'activity_accepted' ||
              notification.type === 'guest_decline' ||
              notification.type === 'guest_decline_pending' ||
              notification.type === 'tentative_response' ||
              notification.type === 'activity_confirmed' ||
              notification.type === 'activity_cancelled' ||
              notification.type === 'socs_summary' ||
              notification.type === 'school_portal_import') {
            const data = notification.data || {};

            // Create system notification
            const systemNotification: SystemNotification = {
              id: notification.id,
              type: notification.type as 'system' | 'activity_reminder' | 'connection_rejected' | 'connection_accepted' | 'activity_rejected' | 'activity_accepted' | 'guest_decline' | 'guest_decline_pending' | 'tentative_response' | 'activity_confirmed' | 'activity_cancelled' | 'socs_summary' | 'school_portal_import',
              title: notification.title,
              message: notification.message,
              timestamp: notification.timestamp,
              data: data,
              child_uuid: notification.child_uuid || data.childUuid || data.child_uuid
            };

            // If it's related to a specific child, add it to that child's list
            if (systemNotification.child_uuid && systemNotificationsByChild[systemNotification.child_uuid]) {
              systemNotificationsByChild[systemNotification.child_uuid].push(systemNotification);
            } else {
              // If no specific child, add to all children (general system message)
              childrenData.forEach(child => {
                systemNotificationsByChild[child.uuid].push(systemNotification);
              });
            }
          }
        });

        setChildSystemNotifications(systemNotificationsByChild);
      } else {
        const systemNotificationsByChild: Record<string, SystemNotification[]> = {};
        childrenData.forEach(child => {
          systemNotificationsByChild[child.uuid] = [];
        });
        setChildSystemNotifications(systemNotificationsByChild);
      }
    } catch (error) {
      console.error('Failed to load system notifications:', error);
      const systemNotificationsByChild: Record<string, SystemNotification[]> = {};
      childrenData.forEach(child => {
        systemNotificationsByChild[child.uuid] = [];
      });
      setChildSystemNotifications(systemNotificationsByChild);
    }
  };

  const handleInvitationResponse = async (invitationUuid: string, action: 'accept' | 'reject') => {
    // Find the invitation and check if it's part of a series
    let currentInvitation: any = null;

    Object.keys(childInvitations).forEach(childId => {
      const invitations = childInvitations[childId] || [];
      const found = invitations.find(inv => inv.invitation_uuid === invitationUuid || String(inv.id) === invitationUuid);
      if (found) {
        currentInvitation = found;
      }
    });

    if (!currentInvitation) {
      console.error('❌ Could not find invitation with UUID:', invitationUuid);
      return;
    }

    // Check if this is a SOCS or Pitchero invitation and action is 'accept'
    if (action === 'accept' && (currentInvitation.source === 'socs' || currentInvitation.source === 'pitchero')) {
      // Show modal to select designated parent
      setPendingInvitationUuid(invitationUuid);
      setPendingInvitationAction(action);
      setShowDesignatedParentModal(true);
      return;
    }

    // OPTIMISTIC UPDATE: Immediately remove from UI and store in localStorage
    try {
      // Remove the invitation from UI immediately
      setChildInvitations(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(childId => {
          updated[childId] = updated[childId].filter(inv => inv.invitation_uuid !== invitationUuid);
        });
        return updated;
      });

      // Store the response in localStorage for background processing
      const pendingResponses = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
      pendingResponses.push({
        invitationUuid,
        action,
        timestamp: Date.now(),
        invitation: currentInvitation
      });
      localStorage.setItem('pendingInvitationResponses', JSON.stringify(pendingResponses));

      // Trigger notification refresh to remove from Dashboard
      window.dispatchEvent(new CustomEvent('notification-refresh'));

      // Process the API call in the background (don't await)
      processInvitationResponseAsync(invitationUuid, action, currentInvitation);

    } catch (error) {
      console.error('Failed to optimistically update invitation:', error);
    }
  };

  // Background async processing of invitation responses
  const processInvitationResponseAsync = async (invitationUuid: string, action: 'accept' | 'reject', currentInvitation: any) => {
    try {

      // Check if this is a TENTATIVE invitation - route to tentative response endpoint
      if (currentInvitation.is_tentative) {
        console.log('🔍 HOME: Handling tentative invitation response (async):', {
          is_tentative: currentInvitation.is_tentative,
          action,
          invitation_uuid: invitationUuid,
          invited_child_uuid: currentInvitation.invited_child_uuid
        });

        let activityUuid = currentInvitation.activity_uuid;
        const childUuid = currentInvitation.invited_child_uuid;

        if (!activityUuid || !childUuid) {
          console.error('❌ Missing data for tentative invitation:', { activityUuid, childUuid });
          return;
        }

        const tentativeResponse = action === 'accept' ? 'interested' : 'not_interested';
        const response = await apiService.respondToTentativeActivity(activityUuid, childUuid, tentativeResponse);

        if (response.success) {
          // Dismiss the notification
          const notificationId = `activity_invitation_${invitationUuid}`;
          await apiService.dismissNotification(notificationId, 'activity_invitation').catch(err =>
            console.error('Failed to dismiss notification:', err)
          );

          // Remove from localStorage pending list
          const pendingResponses = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
          const filtered = pendingResponses.filter((r: any) => r.invitationUuid !== invitationUuid);
          localStorage.setItem('pendingInvitationResponses', JSON.stringify(filtered));

          console.log('✅ Tentative invitation processed successfully');
        } else {
          console.error('❌ Failed to process tentative invitation:', response.error);
        }
        return;
      }

      // Check if this is a recurring series invitation from notification
      if ((currentInvitation as any).isRecurringSeries) {
        const response = await apiService.respondToActivityInvitation(invitationUuid, action);

        if (response.success) {
          // Remove from localStorage pending list
          const pendingResponses = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
          const filtered = pendingResponses.filter((r: any) => r.invitationUuid !== invitationUuid);
          localStorage.setItem('pendingInvitationResponses', JSON.stringify(filtered));

          // Emit event to notify CalendarScreen to refresh
          window.dispatchEvent(new CustomEvent('activityInvitationResponded', {
            detail: { action, activityUuid: currentInvitation.activity_uuid }
          }));

          console.log('✅ Recurring series invitation processed successfully');
        } else {
          console.error(`❌ Failed to ${action} series invitation:`, response.error);
        }
        return;
      }

      // Handle single regular invitation
      const response = await apiService.respondToActivityInvitation(invitationUuid, action);

      if (response.success) {
        // If invitation was accepted, increment club usage
        if (action === 'accept' && currentInvitation) {
          if (currentInvitation.website_url && currentInvitation.activity_type && currentInvitation.activity_uuid) {
            const clubUsageData = {
              website_url: currentInvitation.website_url,
              activity_type: currentInvitation.activity_type,
              location: currentInvitation.location || null,
              activity_uuid: currentInvitation.activity_uuid
            };
            await apiService.incrementClubUsage(clubUsageData).catch(err =>
              console.error('Failed to increment club usage:', err)
            );
          }
        }

        // Remove from localStorage pending list
        const pendingResponses = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
        const filtered = pendingResponses.filter((r: any) => r.invitationUuid !== invitationUuid);
        localStorage.setItem('pendingInvitationResponses', JSON.stringify(filtered));

        // Emit event to notify CalendarScreen to refresh
        window.dispatchEvent(new CustomEvent('activityInvitationResponded', {
          detail: { action, activityUuid: currentInvitation.activity_uuid }
        }));

        console.log('✅ Regular invitation processed successfully');
      } else {
        console.error(`❌ Failed to respond to invitation:`, response.error);
      }
    } catch (error) {
      console.error('Failed to respond to invitation (async):', error);
    }
  };

  const handleDesignatedParentConfirm = async (skip: boolean = false) => {
    if (!skip && !selectedParentUuid) {
      alert('Please select a designated parent');
      return;
    }

    setShowDesignatedParentModal(false);

    // Bulk accept: the parent chosen here applies to every invitation in the batch.
    if (pendingBulkAccept) {
      const list = pendingBulkAccept;
      setPendingBulkAccept(null);
      const childUuid = list[0]?.invited_child_uuid || 'bulk';
      setBulkProcessing(childUuid);
      try {
        await applyBulkResponse(list, 'accept', skip ? '' : selectedParentUuid);
      } finally {
        setBulkProcessing(null);
        setProcessingInvitation(null);
      }
      return;
    }

    try {
      setProcessingInvitation(parseInt(pendingInvitationUuid));

      // Find the invitation again
      let currentInvitation: any = null;

      Object.keys(childInvitations).forEach(childId => {
        const invitations = childInvitations[childId] || [];
        const found = invitations.find(inv => inv.invitation_uuid === pendingInvitationUuid || String(inv.id) === pendingInvitationUuid);
        if (found) {
          currentInvitation = found;
        }
      });

      if (!currentInvitation) {
        console.error('❌ Could not find invitation with UUID:', pendingInvitationUuid);
        setProcessingInvitation(null);
        return;
      }

      // Call API with designated parent UUID (or undefined if skipped)
      const response = await apiService.respondToActivityInvitation(
        pendingInvitationUuid,
        pendingInvitationAction,
        skip ? undefined : selectedParentUuid
      );

      if (response.success) {
        // Remove the invitation from the child's list
        setChildInvitations(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childId => {
            updated[childId] = updated[childId].filter(inv => inv.invitation_uuid !== pendingInvitationUuid);
          });
          return updated;
        });

        // Trigger notification refresh
        window.dispatchEvent(new CustomEvent('notification-refresh'));

        alert('Invitation accepted! Activity will appear in your calendar.');

        // Reload data to refresh activity status (force - user just took action)
        await loadChildren(true);

        // Emit event to notify CalendarScreen to refresh
        window.dispatchEvent(new CustomEvent('activityInvitationResponded', {
          detail: { action: pendingInvitationAction, activityUuid: currentInvitation.activity_uuid }
        }));

        // Scroll to notifications section after response
        setTimeout(() => scrollToNotifications(), 300);
      } else {
        alert(`Error: ${response.error || 'Failed to respond to invitation'}`);
      }
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Failed to respond to invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  // A recurring series shows only its first occurrence — same rule the list
  // render uses, shared so bulk actions act on exactly what's on screen.
  const visibleInvitations = (list: any[]) => list.filter((invitation, index, arr) => {
    if ((invitation as any).isRecurringSeries) {
      const seriesId = (invitation as any).seriesId;
      if (seriesId) {
        const seriesInvitations = arr.filter(inv => (inv as any).seriesId === seriesId);
        const sortedSeries = seriesInvitations.sort((a, b) =>
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        );
        return invitation.id === sortedSeries[0].id;
      }
    }
    return true;
  });

  const invitationKey = (inv: any) => inv.invitation_uuid || String(inv.id);

  // Apply accept/decline to a whole list. Optimistic like the single-item path,
  // but the list is removed in one render and data reloads once at the end.
  const applyBulkResponse = async (
    list: any[],
    action: 'accept' | 'reject',
    designatedParentUuid?: string
  ) => {
    if (!list.length) return;
    const keys = new Set(list.map(invitationKey));

    setChildInvitations(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(childId => {
        updated[childId] = updated[childId].filter(inv => !keys.has(invitationKey(inv)));
      });
      return updated;
    });

    const pendingResponses = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
    list.forEach(inv => pendingResponses.push({
      invitationUuid: invitationKey(inv), action, timestamp: Date.now(), invitation: inv
    }));
    localStorage.setItem('pendingInvitationResponses', JSON.stringify(pendingResponses));
    window.dispatchEvent(new CustomEvent('notification-refresh'));

    for (const inv of list) {
      const uuid = invitationKey(inv);
      try {
        const needsParent = action === 'accept'
          && (inv.source === 'socs' || inv.source === 'pitchero')
          && designatedParentUuid !== undefined;
        if (needsParent) {
          // One designated parent chosen once, applied to every invitation.
          await apiService.respondToActivityInvitation(
            uuid, action, designatedParentUuid || undefined
          );
          const stored = JSON.parse(localStorage.getItem('pendingInvitationResponses') || '[]');
          localStorage.setItem('pendingInvitationResponses',
            JSON.stringify(stored.filter((r: any) => r.invitationUuid !== uuid)));
        } else {
          await processInvitationResponseAsync(uuid, action, inv);
        }
      } catch (error) {
        console.error('Bulk response failed for', uuid, error);
      }
    }

    await loadChildren(true);
    window.dispatchEvent(new CustomEvent('notification-refresh'));
  };

  const handleBulkInvitationResponse = async (childUuid: string, action: 'accept' | 'reject') => {
    const list = visibleInvitations(childInvitations[childUuid] || []);
    if (!list.length) return;

    const verb = action === 'accept' ? 'Accept' : 'Decline';
    if (!window.confirm(`${verb} all ${list.length} invitation${list.length !== 1 ? 's' : ''}?`)) return;

    // Integration invitations need a designated parent — ask once, not per item.
    if (action === 'accept' && list.some(i => i.source === 'socs' || i.source === 'pitchero')) {
      setPendingBulkAccept(list);
      setSelectedParentUuid('');
      setShowDesignatedParentModal(true);
      return;
    }

    setBulkProcessing(childUuid);
    try {
      await applyBulkResponse(list, action);
    } finally {
      setBulkProcessing(null);
    }
  };

  // Snooze all: one server call, and only the notifications the server confirms
  // it wrote are removed from the UI. Firing a POST per card and hiding them all
  // optimistically meant any call that failed came back on the next load.
  const handleBulkSnooze = async (childUuid: string, reminderPeriod: string) => {
    const list = visibleInvitations(childInvitations[childUuid] || [])
      .filter(inv => (inv as any).dismissible !== false && (inv as any).notification_id);
    setBulkSnoozeDropdown(null);
    if (!list.length) return;

    setBulkProcessing(childUuid);
    try {
      const response = await apiService.snoozeAllNotifications(childUuid, reminderPeriod);

      if (!response.success) {
        console.error('Bulk snooze failed:', response.error);
        window.alert('Could not snooze these invitations. Please try again.');
        return;
      }

      const snoozedIds = new Set(
        (response.data?.notifications || []).map(n => n.notification_id)
      );

      // A card the server did not snooze stays on screen rather than silently
      // vanishing until the next reload.
      setChildInvitations(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(cId => {
          updated[cId] = updated[cId].filter(inv => !snoozedIds.has((inv as any).notification_id));
        });
        return updated;
      });

      window.dispatchEvent(new CustomEvent('notification-refresh'));

      const missed = list.filter(inv => !snoozedIds.has((inv as any).notification_id)).length;
      if (missed > 0) {
        console.warn(`⚠️ Bulk snooze: ${missed} of ${list.length} invitation(s) were not snoozed`);
      }
    } catch (error) {
      console.error('Bulk snooze failed:', error);
      window.alert('Could not snooze these invitations. Please try again.');
    } finally {
      setBulkProcessing(null);
    }
  };

  const handleDismissInvitation = async (notificationId: string, reminderPeriod: string) => {
    // OPTIMISTIC UPDATE: Immediately remove from UI and store in localStorage
    try {
      setShowDismissDropdown(null);

      // Remove from UI immediately by notification_id
      setChildInvitations(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(childId => {
          updated[childId] = updated[childId].filter(inv => (inv as any).notification_id !== notificationId);
        });
        return updated;
      });

      // Store in localStorage for retry on failure
      const pendingDismissals = JSON.parse(localStorage.getItem('pendingInvitationDismissals') || '[]');
      pendingDismissals.push({ notificationId, reminderPeriod, timestamp: Date.now() });
      localStorage.setItem('pendingInvitationDismissals', JSON.stringify(pendingDismissals));

      window.dispatchEvent(new CustomEvent('notification-refresh'));

      // Process the API call in the background
      processSnoozeAsync(notificationId, reminderPeriod, 0);

    } catch (error) {
      console.error('Failed to optimistically dismiss notification:', error);
    }
  };

  // Background async snooze with retry
  const processSnoozeAsync = async (notificationId: string, reminderPeriod: string, retryCount: number = 0) => {
    try {
      const response = await apiService.snoozeNotification(notificationId, reminderPeriod);

      if (response.success && (response.data?.rows_updated ?? 1) > 0) {
        // Persisted to DB - remove from localStorage
        const pendingDismissals = JSON.parse(localStorage.getItem('pendingInvitationDismissals') || '[]');
        const filtered = pendingDismissals.filter((d: any) => d.notificationId !== notificationId);
        localStorage.setItem('pendingInvitationDismissals', JSON.stringify(filtered));
        console.log('✅ Notification snoozed and persisted to DB');
      } else if (retryCount < 2) {
        console.warn('⚠️ Snooze did not persist, retrying...');
        setTimeout(() => processSnoozeAsync(notificationId, reminderPeriod, retryCount + 1), 1000 * (retryCount + 1));
      }
    } catch (error) {
      console.error('Failed to snooze notification:', error);
      if (retryCount < 2) {
        setTimeout(() => processSnoozeAsync(notificationId, reminderPeriod, retryCount + 1), 1000 * (retryCount + 1));
      }
    }
  };

  const handleDismissActivity = async (activityUuid: string) => {
    if (!window.confirm('Mark this activity as done? It will no longer show in today/tomorrow but will remain in your calendar.')) {
      return;
    }

    try {
      setDeletingActivity(activityUuid);
      const response = await apiService.dismissActivity(activityUuid);

      if (response.success) {
        // Remove activity from today/tomorrow lists
        setTodayActivities(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childUuid => {
            updated[childUuid] = updated[childUuid].filter(act => {
              const actUuid = act.activity_uuid || (act as any).uuid;
              return actUuid !== activityUuid;
            });
          });
          return updated;
        });

        setTomorrowActivities(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childUuid => {
            updated[childUuid] = updated[childUuid].filter(act => {
              const actUuid = act.activity_uuid || (act as any).uuid;
              return actUuid !== activityUuid;
            });
          });
          return updated;
        });

        // Reload to refresh all data (force - user just took action)
        await loadChildren(true);
      } else {
        alert(`Error: ${response.error || 'Failed to mark activity as done'}`);
      }
    } catch (error) {
      console.error('Failed to mark activity as done:', error);
      alert('Failed to mark activity as done');
    } finally {
      setDeletingActivity(null);
    }
  };

  const handleConnectionResponse = async (connectionUuid: string, action: 'accept' | 'reject') => {
    // OPTIMISTIC UPDATE: Immediately remove from UI and store in localStorage
    try {
      // Remove the connection request from UI immediately
      setChildConnections(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(childId => {
          updated[childId] = updated[childId].filter(req => req.request_uuid !== connectionUuid);
        });
        return updated;
      });

      // Store the response in localStorage for background processing
      const pendingConnections = JSON.parse(localStorage.getItem('pendingConnectionResponses') || '[]');
      pendingConnections.push({
        connectionUuid,
        action,
        timestamp: Date.now()
      });
      localStorage.setItem('pendingConnectionResponses', JSON.stringify(pendingConnections));

      // Process the API call in the background (don't await)
      processConnectionResponseAsync(connectionUuid, action);

    } catch (error) {
      console.error('Failed to optimistically update connection:', error);
    }
  };

  // Background async processing of connection responses
  const processConnectionResponseAsync = async (connectionUuid: string, action: 'accept' | 'reject') => {
    try {
      const response = await apiService.respondToConnectionRequest(connectionUuid, action);

      if (response.success) {
        // Remove from localStorage pending list
        const pendingConnections = JSON.parse(localStorage.getItem('pendingConnectionResponses') || '[]');
        const filtered = pendingConnections.filter((r: any) => r.connectionUuid !== connectionUuid);
        localStorage.setItem('pendingConnectionResponses', JSON.stringify(filtered));

        console.log('✅ Connection request processed successfully');
      } else {
        console.error(`❌ Failed to respond to connection request:`, response.error);
      }
    } catch (error) {
      console.error('Failed to respond to connection request (async):', error);
    }
  };

  const getActivityColor = (activity: any) => {
    // Priority: Pitchero invitations > regular invitations > cancelled/can't attend > shared/private status

    // 1. Pitchero pending invitations - green
    if (activity.source === 'pitchero' && activity.isPendingInvitation) {
      return {
        background: 'linear-gradient(135deg, #48bb78, #68d391)', // Green for pending
        borderColor: '#48bb78'
      };
    }

    // 2. Pitchero accepted invitations - dark grey (confirmed)
    if (activity.source === 'pitchero' && activity.isAcceptedInvitation) {
      return {
        background: 'linear-gradient(135deg, #2d3748, #4a5568)', // Dark grey for accepted/confirmed
        borderColor: '#2d3748'
      };
    }

    // 3. Regular pending invitations - green
    if (activity.isPendingInvitation) {
      return {
        background: 'linear-gradient(135deg, #48bb78, #68d391)', // Green for pending
        borderColor: '#48bb78'
      };
    }

    // 4. Regular accepted invitations - light blue
    if (activity.isAcceptedInvitation) {
      return {
        background: 'linear-gradient(135deg, #4299e1, #63b3ed)', // Blue for shared/accepted
        borderColor: '#4299e1'
      };
    }

    // 5. Declined invitations OR host can't attend - light grey
    if (activity.isDeclinedInvitation || activity.is_cancelled) {
      return {
        background: 'linear-gradient(135deg, #a0aec0, #cbd5e0)', // Light grey for declined/can't attend
        borderColor: '#a0aec0'
      };
    }

    // 6. For own activities (not invitations), check if shared or private
    if (activity.is_shared) {
      return {
        background: 'linear-gradient(135deg, #4299e1, #63b3ed)', // Light blue for shared (host)
        borderColor: '#4299e1'
      };
    }

    // 7. Private activities (default) - dark grey
    return {
      background: 'linear-gradient(135deg, #2d3748, #4a5568)',
      borderColor: '#2d3748'
    };
  };

  const handleKitToggle = async (activityUuid: string, childUuid: string, itemName: string, currentlyPacked: boolean) => {
    // Optimistic update
    const newPackedState = !currentlyPacked;
    const trackingKey = `${activityUuid}-${childUuid}`;

    setKitTracking(prev => ({
      ...prev,
      [trackingKey]: {
        ...prev[trackingKey],
        [itemName]: newPackedState
      }
    }));

    // Persist to backend
    try {
      await apiService.updateKitTracking(activityUuid, childUuid, itemName, newPackedState);

      // Update the activity's kit_tracking field in both activity arrays
      const updateActivityKitTracking = (activity: any) => {
        if (activity.uuid === activityUuid || activity.activity_uuid === activityUuid) {
          const updatedKitTracking = { ...(activity.kit_tracking || {}) };
          if (!updatedKitTracking[trackingKey]) {
            updatedKitTracking[trackingKey] = {};
          }
          updatedKitTracking[trackingKey][itemName] = newPackedState;
          return { ...activity, kit_tracking: updatedKitTracking };
        }
        return activity;
      };

      setTodayActivities(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(childId => {
          updated[childId] = updated[childId].map(updateActivityKitTracking);
        });
        return updated;
      });

      setTomorrowActivities(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(childId => {
          updated[childId] = updated[childId].map(updateActivityKitTracking);
        });
        return updated;
      });
    } catch (error) {
      console.error('Failed to update kit tracking:', error);
      // Revert on error
      setKitTracking(prev => ({
        ...prev,
        [trackingKey]: {
          ...prev[trackingKey],
          [itemName]: currentlyPacked
        }
      }));
    }
  };

  // Open configuration modal for an activity
  const handleOpenConfigureModal = (activity: CalendarActivity) => {
    setConfigureActivity(activity);
    setConfigureKitItems(activity.kit_needed || []);
    setConfigureParentUuid(activity.responsible_parent_uuid || '');
    setConfigureReminderMinutes((activity as any).reminder_minutes ?? null);
    setConfigureNewKitItem('');
    setShowConfigureModal(true);
  };

  // Save activity configuration
  const handleSaveConfiguration = async (applyToSeries: boolean = false) => {
    if (!configureActivity) return;

    setConfigureSaving(true);
    setShowSeriesPrompt(false);

    try {
      // Request alarm permission proactively if setting a reminder
      // (must happen in direct response to user tap for iOS to show the prompt)
      if (configureReminderMinutes !== null && Capacitor.isNativePlatform()) {
        const localNotifService = LocalNotificationService.getInstance();
        await localNotifService.requestAlarmPermission();
      }

      const activityUuid = configureActivity.activity_uuid || (configureActivity as any).uuid;
      const response = await apiService.configureActivity(activityUuid, {
        kit_needed: configureKitItems,
        responsible_parent_uuid: configureParentUuid || null,
        reminder_minutes: configureReminderMinutes,
        apply_to_series: applyToSeries
      });

      if (response.success) {
        // Schedule/cancel local alarm if on native platform
        if (Capacitor.isNativePlatform()) {
          const localNotificationService = LocalNotificationService.getInstance();
          if (configureReminderMinutes !== null) {
            await localNotificationService.cancelReminder(activityUuid);
            const alarmResult = await localNotificationService.scheduleReminder({
              activityUuid,
              activityName: configureActivity.name,
              startDate: configureActivity.start_date,
              startTime: configureActivity.start_time || '00:00',
              endTime: configureActivity.end_time || '',
              minutesBefore: configureReminderMinutes,
              reminderTarget: (configureActivity as any).reminder_target || 'start',
              childName: configureActivity.child_name,
              location: configureActivity.location
            });
            // Show user which alarm type was scheduled
            if (alarmResult) {
              const alarmType = alarmResult.useAlarmKit ? 'AlarmKit alarm' : 'local notification';
              console.log(`🔔 Reminder saved as ${alarmType} for "${configureActivity.name}"`);
            }
          } else {
            await localNotificationService.cancelReminder(activityUuid);
          }
        }

        // Update local state to reflect changes
        const updateActivityConfig = (act: CalendarActivity) => {
          const actUuid = act.activity_uuid || (act as any).uuid;
          if (actUuid === activityUuid || (applyToSeries && (act as any).series_id === (configureActivity as any).series_id)) {
            return {
              ...act,
              kit_needed: configureKitItems,
              responsible_parent_uuid: configureParentUuid || null,
              responsible_parent_first_name: parents.find(p => p.uuid === configureParentUuid)?.username || '',
              responsible_parent_last_name: '',
              reminder_minutes: configureReminderMinutes
            } as CalendarActivity;
          }
          return act;
        };

        setTodayActivities(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childId => {
            updated[childId] = updated[childId].map(updateActivityConfig);
          });
          return updated;
        });

        setTomorrowActivities(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childId => {
            updated[childId] = updated[childId].map(updateActivityConfig);
          });
          return updated;
        });

        setShowConfigureModal(false);
        setConfigureActivity(null);
      } else {
        alert('Failed to save configuration: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Failed to save activity configuration:', error);
      alert('Failed to save configuration');
    } finally {
      setConfigureSaving(false);
    }
  };

  // Handle save button click - check if series prompt needed
  const handleConfigureSaveClick = () => {
    if (!configureActivity) return;

    // Check if activity is part of a series
    const hasSeries = (configureActivity as any).series_id;

    if (hasSeries) {
      // Show prompt to ask about applying to series
      setShowSeriesPrompt(true);
    } else {
      // Single activity, just save
      handleSaveConfiguration(false);
    }
  };

  // Add kit item
  const handleAddKitItem = () => {
    const item = configureNewKitItem.trim();
    if (item && !configureKitItems.includes(item)) {
      setConfigureKitItems([...configureKitItems, item]);
      setConfigureNewKitItem('');
    }
  };

  // Remove kit item
  const handleRemoveKitItem = (index: number) => {
    setConfigureKitItems(configureKitItems.filter((_, i) => i !== index));
  };

  // Check if activity is a drop-off (morning activity before 10:00)
  const isDropOffActivity = (activity: CalendarActivity | null): boolean => {
    if (!activity?.start_time) return false;
    const hour = parseInt(activity.start_time.split(':')[0], 10);
    return hour < 10; // Morning activities before 10:00 are considered drop-offs
  };

  // Uniform options matching term planner
  const uniformOptions = ['Smart Uniform', 'PE Kit', 'Home Clothes', 'Costume'];

  const formatTime = (activity: any) => {
    if (!activity.start_time && !activity.end_time) return '';

    // Remove seconds from time (HH:MM:SS -> HH:MM)
    const formatTimeString = (time: string) => {
      if (!time) return '';
      // If format is HH:MM:SS, return HH:MM
      const parts = time.split(':');
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
    };

    // For pickup activities, show end_time (the actual pickup time)
    const activityName = (activity.name || '').toLowerCase();
    const isPickUp = activityName.includes('pick up') || activityName.includes('pickup');
    if (isPickUp && activity.end_time) {
      return formatTimeString(activity.end_time);
    }

    if (!activity.start_time) return '';
    const startTime = formatTimeString(activity.start_time);
    const endTime = activity.end_time ? formatTimeString(activity.end_time) : '';

    return endTime ? `${startTime}-${endTime}` : startTime;
  };

  const formatActivityNameAndTime = (activity: any) => {
    const time = formatTime(activity);
    const activityName = activity.name || '';

    // For activities, show name and time
    return `${activityName} ${time}`;
  };

  const ensureProtocol = (url: string) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return 'https://' + url;
  };

  const handleActivityClick = (activity: CalendarActivity, child: Child | null) => {
    // Use onNavigateToActivity if available (same logic as CalendarScreen)
    // For parent activities (no child), we still navigate to activity detail
    if (onNavigateToActivity && child) {
      onNavigateToActivity(child, activity);
    }
    // TODO: Handle parent activity clicks when child is null
  };

  const toggleSectionCollapse = (sectionKey: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const renderCollapsedActivityCard = (activity: CalendarActivity, child: Child | null, activityColors: { background: string }) => {
    const activityUuid = activity.activity_uuid || (activity as any).uuid;

    return (
      <div
        key={activityUuid}
        onClick={() => handleActivityClick(activity, child)}
        style={{
          background: activityColors.background,
          color: 'white',
          marginBottom: '4px',
          padding: '8px 12px',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <span style={{ fontSize: '14px', fontWeight: '500', textDecoration: (activity as any).is_cancelled ? 'line-through' : 'none' }}>
          {formatActivityNameAndTime(activity)}
        </span>
        <span style={{ fontSize: '12px', opacity: 0.8 }}>›</span>
      </div>
    );
  };

  const renderActivityCard = (activity: CalendarActivity, child: Child | null, isParentActivity: boolean = false) => {
    const activityUuid = activity.activity_uuid || (activity as any).uuid;

    // Determine if this is an invited activity
    const isInvitedActivity = (activity as any).isAcceptedInvitation || (activity as any).isPendingInvitation;

    // For invited activities, use invited_child_uuid (YOUR child who was invited)
    // For own activities, use child_uuid (YOUR child who is the host)
    const childUuid = isInvitedActivity
      ? (activity.invited_child_uuid || activity.child_uuid)
      : activity.child_uuid;

    // Get activity color based on type
    const activityColors = getActivityColor(activity);
    let activitySource = '';
    if (isInvitedActivity) {
      // Check if this is a Pitchero activity
      if ((activity as any).source === 'pitchero') {
        activitySource = 'Invited by Pitchero';
      } else {
        const hostChildName = (activity as any).host_child_name || (activity as any).child_name || 'Friend';
        // Only show "Invited by" if the host is NOT one of the parent's own children
        const isOwnChildHost = children.some(c => c.name === hostChildName);
        if (!isOwnChildHost) {
          activitySource = `Invited by ${hostChildName}`;
        }
      }
    }

    return (
      <div key={activityUuid} style={{
        background: activityColors.background,
        color: 'white',
        marginBottom: '8px',
        padding: '10px',
        position: 'relative',
        borderRadius: '4px',
        minHeight: '80px',
        overflow: 'hidden'
      }}>
        {/* Dismiss Button - top-left */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismissActivity(activityUuid);
          }}
          disabled={deletingActivity === activityUuid}
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            fontSize: '14px',
            cursor: deletingActivity === activityUuid ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            zIndex: 10,
            fontWeight: 'bold',
            opacity: deletingActivity === activityUuid ? 0.5 : 1
          }}
          onMouseOver={(e) => {
            if (deletingActivity !== activityUuid) {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          title="Dismiss activity"
        >
          {deletingActivity === activityUuid ? '...' : '✕'}
        </button>

        {/* Status Badge and View Button - top-right */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          alignItems: 'flex-end',
          zIndex: 10 // Ensure button stays on top of kit section
        }}>
          <div style={{
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '12px',
            fontWeight: 'bold',
            backgroundColor:
              (activity as any).is_cancelled ? '#DC3545' : // Red for cancelled
              (activity as any).isPendingInvitation ? '#FFA500' : // Orange for pending
              (activity as any).isDeclinedInvitation ? '#DC3545' : // Red for rejected
              (activity as any).isAcceptedInvitation ? '#28A745' : // Green for accepted
              (activity as any).is_shared ? '#007BFF' : // Blue for shared
              '#6C757D', // Gray for private
            color: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            {(activity as any).is_cancelled ? 'Cancelled' :
             (activity as any).isPendingInvitation ? 'Pending' :
             (activity as any).isDeclinedInvitation ? 'Rejected' :
             (activity as any).isAcceptedInvitation ? 'Accepted' :
             (activity as any).is_shared ? 'Shared' : 'Private'}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleActivityClick(activity, child);
            }}
            style={{
              fontSize: '12px',
              padding: '6px 12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)', // Much more opaque for better visibility
              color: activityColors.background, // Use card color for contrast
              border: '2px solid white',
              borderRadius: '16px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 3px 8px rgba(0,0,0,0.3)', // Stronger shadow
              minWidth: '50px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            View
          </button>
        </div>

        {/* Activity Name + Time */}
        <div style={{
          fontSize: '15px',
          fontWeight: 'bold',
          marginBottom: '4px',
          color: 'white',
          paddingLeft: '32px', // Make space for dismiss button
          paddingRight: '100px', // Make space for status badge
          textDecoration: (activity as any).is_cancelled ? 'line-through' : 'none'
        }}>
          {formatActivityNameAndTime(activity)}
        </div>

        {/* Website URL - directly below title (but not for School Day which has lessons in description) */}
        {activity.website_url && activity.name !== 'School Day' && (
          <div style={{
            marginBottom: '6px',
            fontSize: '13px',
            color: 'rgba(255, 255, 255, 0.95)'
          }}>
            🌐 <a href={ensureProtocol(activity.website_url)} target="_blank" rel="noopener noreferrer"
               style={{ color: 'rgba(255, 255, 255, 0.95)', textDecoration: 'underline', wordBreak: 'break-all' }}>
              {activity.website_url}
            </a>
          </div>
        )}

        {/* Description - show for activities with description (like School Day lessons) */}
        {activity.description && (
          <div style={{
            marginBottom: '6px',
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontStyle: 'italic',
            lineHeight: '1.4',
            paddingLeft: '32px',
            paddingRight: '80px'
          }}>
            📚 {activity.description}
          </div>
        )}

        {/* Activity Details */}
        <div style={{
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.95)'
        }}>
          {(activity.venue || activity.city || activity.location || activity.contact_phone || activity.contact_email || (activity as any).club_contact_phone || (activity as any).club_contact_email) && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginBottom: '4px'
            }}>
              {(activity.venue || activity.location || activity.city) && (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {(activity.venue || activity.location) && <span>🏛️ {activity.venue || activity.location}</span>}
                  {activity.city && activity.city !== (activity.venue || activity.location) && <span>{(activity.venue || activity.location) ? '•' : ''} 📍 {activity.city}</span>}
                </div>
              )}
              {(activity.contact_phone || (activity as any).club_contact_phone) && (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span>📞 {activity.contact_phone || (activity as any).club_contact_phone}</span>
                </div>
              )}
              {(activity.contact_email || (activity as any).club_contact_email) && (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span>📧 {activity.contact_email || (activity as any).club_contact_email}</span>
                </div>
              )}
              {activity.passcode && !(activity as any).isAcceptedInvitation && !(activity as any).isPendingInvitation && (
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  alignItems: 'center',
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  marginTop: '4px',
                  fontWeight: 'bold'
                }}>
                  <span>🔐 Passcode: {activity.passcode}</span>
                </div>
              )}
            </div>
          )}

          {/* Invited by */}
          {activitySource && (
            <div style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: '500',
              marginTop: '4px',
              fontSize: '12px'
            }}>
              {activitySource}
            </div>
          )}

          {/* Responsible Parent */}
          {(activity as any).responsible_parent_uuid && (
            <div style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: '600',
              marginTop: '6px',
              fontSize: '13px',
              padding: '6px 10px',
              background: (activity as any).responsible_parent_uuid === user?.uuid
                ? 'rgba(255, 255, 255, 0.25)'
                : 'rgba(255, 255, 255, 0.15)',
              borderRadius: '6px',
              display: 'inline-block',
              border: (activity as any).responsible_parent_uuid === user?.uuid
                ? '2px solid rgba(255, 255, 255, 0.5)'
                : 'none'
            }}>
              🚗 Designated Parent: {`${(activity as any).responsible_parent_first_name || ''} ${(activity as any).responsible_parent_last_name || ''}`.trim() || 'Not specified'}
            </div>
          )}
        </div>

        {/* Interactive Kit Checklist - same as CalendarScreen */}
        {activity.kit_needed && Array.isArray(activity.kit_needed) && activity.kit_needed.length > 0 && (() => {
          // Get tracking from activity.kit_tracking field (from backend)
          const activityKitTracking = (activity as any).kit_tracking || {};
          // Key is activityUuid-childUuid
          const trackingKey = `${activityUuid}-${childUuid}`;
          const childTracking = activityKitTracking[trackingKey] || {};
          // Merge with local state for optimistic updates
          const localTracking = kitTracking[trackingKey] || {};
          const trackingForActivity = { ...childTracking, ...localTracking };

          return (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: '#f5f5f5',
              borderRadius: '6px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>🎒 Kit Needed:</div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                {activity.kit_needed.map((item: string, index: number) => {
                  const isPacked = trackingForActivity[item] || false;
                  return (
                    <label key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#333',
                      cursor: 'pointer',
                      padding: '4px 0'
                    }}>
                      <input
                        type="checkbox"
                        checked={isPacked}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (activityUuid && childUuid) {
                            handleKitToggle(activityUuid, childUuid, item, isPacked);
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <span style={{
                        textDecoration: isPacked ? 'line-through' : 'none',
                        opacity: isPacked ? 0.6 : 1
                      }}>
                        {item}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Configure button - show when kit OR parent is missing */}
        {(!activity.kit_needed || activity.kit_needed.length === 0 || !(activity as any).responsible_parent_uuid) && (
          <div style={{
            marginTop: '12px',
            padding: '10px',
            background: '#e3f2fd',
            borderRadius: '6px',
            border: '1px solid #1976d2'
          }}>
            <div style={{
              fontSize: '13px',
              color: '#1565c0',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {!activity.kit_needed || activity.kit_needed.length === 0 ? (isDropOffActivity(activity) ? '👔 No uniform added' : '🎒 No kit items added') : ''}
              {(!activity.kit_needed || activity.kit_needed.length === 0) && !(activity as any).responsible_parent_uuid ? ' • ' : ''}
              {!(activity as any).responsible_parent_uuid ? '🚗 No parent assigned' : ''}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenConfigureModal(activity);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ⚙️ Add Kit & Parent
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="children-screen">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Check if user has any activities at all (new user detection)
  const hasAnyActivities = Object.values(todayActivities).some(acts => acts.length > 0) ||
    Object.values(tomorrowActivities).some(acts => acts.length > 0) ||
    Object.values(todayParentActivities).some(acts => acts.length > 0) ||
    Object.values(tomorrowParentActivities).some(acts => acts.length > 0);

  console.log('🔍 Welcome Notification Debug:', {
    childrenCount: children.length,
    hasAnyActivities,
    welcomeNotificationDismissed,
    todayActivitiesCount: Object.values(todayActivities).flat().length,
    tomorrowActivitiesCount: Object.values(tomorrowActivities).flat().length,
    shouldShow: children.length > 0 && !welcomeNotificationDismissed
  });

  return (
    <div className="children-screen">
      <div className="children-header">
        <h2>Home</h2>
      </div>

      {/* Welcome notification - shows when it exists in bell API */}
      {children.length > 0 && !welcomeNotificationDismissed && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          position: 'relative'
        }}>
          <button
            onClick={handleDismissWelcomeNotification}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            ✕
          </button>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '12px',
            margin: '0 0 12px 0',
            color: 'white',
            paddingRight: '40px'
          }}>
            👋 Welcome to Cluubz!
          </h3>
          <p style={{
            fontSize: '15px',
            marginBottom: '16px',
            lineHeight: '1.5',
            opacity: 0.95
          }}>
            You're all set up! Ready to organize your first activity? Click the button below to get started with your family's calendar.
          </p>
          <button
            onClick={async () => {
              await handleDismissWelcomeNotification();
              // Navigate to calendar
              if (onNavigateToChildCalendar && children.length > 0) {
                onNavigateToChildCalendar(children[0]);
              }
            }}
            style={{
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
          >
            📅 View Calendar & Create Activity
          </button>
        </div>
      )}

      {/* Alarm Permission Banner - shown when activities have reminders but AlarmKit not granted */}
      {showAlarmPermissionBanner && !alarmPermissionGranted && (
        <div style={{
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          border: '1px solid #64b5f6',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: '#1565c0', fontSize: '15px', marginBottom: '4px' }}>
              Enable Alarm Reminders
            </div>
            <div style={{ color: '#1976d2', fontSize: '13px' }}>
              Your activities have reminders set. Allow alarms so you get notified on time.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={async () => {
                const localNotifService = LocalNotificationService.getInstance();
                const granted = await localNotifService.requestAlarmPermission();
                if (granted) {
                  setAlarmPermissionGranted(true);
                  setShowAlarmPermissionBanner(false);

                  // Clear stale stored reminders (OS alarms are gone after reinstall)
                  // then schedule fresh for all activities with reminders
                  try {
                    await localNotifService.clearAllReminders();
                    const now = new Date();
                    const farFuture = new Date(now);
                    farFuture.setFullYear(farFuture.getFullYear() + 1);
                    const calResponse = await apiService.getCalendarActivities(
                      now.toISOString().split('T')[0],
                      farFuture.toISOString().split('T')[0]
                    );
                    if (calResponse.success && calResponse.data) {
                      const upcoming = Array.isArray(calResponse.data) ? calResponse.data : [];
                      for (const a of upcoming) {
                        if (a.reminder_minutes != null && a.reminder_minutes >= 0) {
                          const actUuid = a.activity_uuid || a.uuid;
                          try {
                            // Cancel any existing local notification before scheduling as AlarmKit
                            await localNotifService.cancelReminder(actUuid);
                            await localNotifService.scheduleReminder({
                              activityUuid: actUuid,
                              activityName: a.name,
                              startDate: a.start_date,
                              startTime: a.start_time || '00:00',
                              endTime: a.end_time || '',
                              minutesBefore: a.reminder_minutes,
                              reminderTarget: a.reminder_target || 'start',
                              childName: a.child_name || a.childName || '',
                            });
                          } catch (e) {
                            console.log('Failed to schedule alarm for', actUuid, e);
                          }
                        }
                      }
                    }
                  } catch (e) {
                    console.log('Failed to schedule alarms after enabling:', e);
                  }
                }
              }}
              style={{
                backgroundColor: '#1565c0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Enable
            </button>
            <button
              onClick={() => setShowAlarmPermissionBanner(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#1565c0',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px',
                lineHeight: 1
              }}
              title="Dismiss"
            >
              x
            </button>
          </div>
        </div>
      )}

      {/* School year changed — the portal has moved this child up a year. The
          filter is already corrected, but their events were imported under the
          old year, so the calendar has to be reloaded to pull the new ones.
          Amber: something needs doing. */}
      {yearGroupChanges.length > 0 && (
        <div style={{ marginBottom: '24px', padding: '0 12px' }}>
          {yearGroupChanges.map(({ childUuid, childName, previousYearGroup, newYearGroup, schoolName }) => (
            <div key={childUuid} style={{
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
              border: '1px solid #ffb300',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontWeight: '600', color: '#e65100', fontSize: '15px', marginBottom: '4px' }}>
                  🎓 {childName} has moved{newYearGroup ? ` up to ${newYearGroup}` : ' up a year'}
                </div>
                <div style={{ color: '#ef6c00', fontSize: '13px' }}>
                  {readableSchoolName(schoolName)} now lists {childName}
                  {previousYearGroup && newYearGroup ? ` in ${newYearGroup}, not ${previousYearGroup}` : ''}.
                  {' '}Reload the School Portal calendar to pick up this year's clubs and events.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => handleReloadSchoolPortalCalendar(childUuid)}
                  disabled={reloadingCalendarFor === childUuid}
                  style={{
                    background: reloadingCalendarFor === childUuid ? '#bdbdbd' : '#ef6c00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: reloadingCalendarFor === childUuid ? 'default' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {reloadingCalendarFor === childUuid ? 'Reloading…' : 'Reload calendar'}
                </button>
                <button
                  onClick={() => handleDismissYearGroupChange(childUuid)}
                  aria-label={`Dismiss year group change for ${childName}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef6c00',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SOCS setup has drifted — a new sport the parent never got offered, or a
          year change that leaves the child on last year's team. We show what
          changed but never guess the answer: only the parent knows which side
          their child has been picked for. Amber: something needs doing. */}
      {socsReviewPrompts.length > 0 && (
        <div style={{ marginBottom: '24px', padding: '0 12px' }}>
          {socsReviewPrompts.map(({ promptUuid, childUuid, childName, schoolName, reason, subject }) => (
            <div key={promptUuid} style={{
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
              border: '1px solid #ffb300',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontWeight: '600', color: '#e65100', fontSize: '15px', marginBottom: '4px' }}>
                  {reason === 'new_sport'
                    ? `🆕 ${socsSchoolLabel(schoolName)} has added ${subject}`
                    : `🏅 Check ${childName}'s sports teams for ${subject}`}
                </div>
                <div style={{ color: '#ef6c00', fontSize: '13px' }}>
                  {reason === 'new_sport'
                    ? `It wasn't there when you set ${childName} up, so it won't appear until you add it.`
                    : `Their sports teams are age based and don't move up with them, so check which ones ${childName} is in now.`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => handleAmendSocsSetup(childUuid)}
                  style={{
                    background: '#ef6c00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Check teams
                </button>
                <button
                  onClick={() => handleDismissSocsPrompt(promptUuid)}
                  aria-label={`Dismiss ${reason === 'new_sport' ? subject : 'year change'} prompt for ${childName}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef6c00',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reopened from a review prompt, scoped to the flagged child. */}
      {socsReviewChild && (
        <SOCSSetupModal
          isOpen={true}
          onClose={handleSocsSetupClosed}
          children={[socsReviewChild]}
        />
      )}

      {/* Upcoming Term Planner Banner — a planner is queued but has not taken
          over yet. Informational only: today's schedule is still the old one.
          Mirrors the expired-planner card, in blue rather than amber, because
          nothing is wrong. Shares the same dismissal store. */}
      {upcomingTermPlannerChildren.filter(c => !termPlannerBannerDismissed.has(c.childUuid)).length > 0 && (
        <div style={{ marginBottom: '24px', padding: '0 12px' }}>
          {upcomingTermPlannerChildren
            .filter(c => !termPlannerBannerDismissed.has(c.childUuid))
            .map(({ childUuid, childName, startDate }) => (
            <div key={childUuid} style={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: '1px solid #64b5f6',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#0d47a1', fontSize: '15px', marginBottom: '4px' }}>
                  {childName}'s schedule changes on {formatDayMonthUK(startDate)}
                </div>
                <div style={{ color: '#1565c0', fontSize: '13px' }}>
                  A new planner takes over that day. Nothing changes before then.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => {
                    if (onNavigateToTermPlanner) {
                      onNavigateToTermPlanner(childUuid);
                    }
                  }}
                  style={{
                    backgroundColor: '#1565c0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  View
                </button>
                <button
                  onClick={() => {
                    const updated = new Set(termPlannerBannerDismissed);
                    updated.add(childUuid);
                    setTermPlannerBannerDismissed(updated);
                    localStorage.setItem('dismissed_term_planner_banners', JSON.stringify(Array.from(updated)));
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1565c0',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px',
                    lineHeight: 1
                  }}
                  title="Dismiss"
                >
                  x
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Holiday Mode Toggle */}
      {children.filter(c => !(c as any).is_parent).length > 0 && (
        <div style={{ marginBottom: '16px', padding: '0 12px' }}>
          <div style={{
            fontSize: '13px',
            color: '#888',
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            Holiday Mode — pause planner activities & reminders
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {children.filter(c => !(c as any).is_parent).map(child => {
              const isOnHoliday = (holidayPauses[child.uuid] || []).length > 0;
              const isToggling = togglingHoliday === child.uuid;
              return (
                <button
                  key={child.uuid}
                  onClick={() => {
                    if (isOnHoliday) {
                      // Coming back from holiday is the moment a parent finds out
                      // the new term's timetable, so ask rather than silently
                      // resuming last term's schedule.
                      setHolidayReturnChild({ uuid: child.uuid, name: child.name });
                    } else {
                      const confirmed = window.confirm(
                        `Turn on holiday mode for ${child.name}?\n\nThis will pause all their recurring planner activities and silence reminders until you turn it off.`
                      );
                      if (confirmed) toggleHolidayMode(child.uuid, child.name);
                    }
                  }}
                  disabled={isToggling}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: isOnHoliday ? '2px solid #e65100' : '1px solid #ddd',
                    backgroundColor: isOnHoliday ? '#fff3e0' : '#f8f9fa',
                    color: isOnHoliday ? '#e65100' : '#555',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isToggling ? 'wait' : 'pointer',
                    opacity: isToggling ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isOnHoliday ? '🏖️' : '🏫'} {child.name}
                  </span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: isOnHoliday ? '#e65100' : '#999'
                  }}>
                    {isToggling ? 'Updating...' : isOnHoliday ? 'On Holiday' : 'At School'}
                    <span style={{
                      width: '36px',
                      height: '20px',
                      borderRadius: '10px',
                      backgroundColor: isOnHoliday ? '#e65100' : '#ccc',
                      position: 'relative',
                      display: 'inline-block',
                      transition: 'background-color 0.2s ease',
                      flexShrink: 0
                    }}>
                      <span style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        position: 'absolute',
                        top: '2px',
                        left: isOnHoliday ? '18px' : '2px',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Today's Activities Section */}
      <div style={{
        marginBottom: hasAnyActivities ? '32px' : '16px',
        padding: '0 12px',
        overflowX: 'hidden'
      }}>
        <h3 style={{
          fontSize: hasAnyActivities ? '20px' : '18px',
          fontWeight: 'bold',
          marginBottom: hasAnyActivities ? '16px' : '8px',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '14px'
          }}>Today</span>
        </h3>
        {children.length === 0 && Object.keys(todayParentActivities).length === 0 ? (
          <div style={{ padding: hasAnyActivities ? '16px' : '8px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            No children added yet
          </div>
        ) : Object.values(todayActivities).every(acts => acts.length === 0) && Object.values(todayParentActivities).every(acts => acts.length === 0) ? (
          <div style={{ padding: hasAnyActivities ? '16px' : '8px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            No activities scheduled for today
          </div>
        ) : (
          <>
            {/* Child activities */}
            {children.map((child, childIndex) => {
              const activities = todayActivities[child.uuid] || [];
              if (activities.length === 0) return null;

              // Muted child colors - index-based to guarantee different colors for siblings
              const childColors = [
                '#5b8cb8', // Muted blue
                '#6a9a6a', // Muted green
                '#b87d7d', // Muted red/rose
                '#c4956a', // Muted orange/tan
                '#8a7ab8', // Muted purple
                '#b87aa8', // Muted pink
                '#5a9a9a', // Muted teal
                '#a8a05a'  // Muted olive/yellow
              ];
              const childColor = childColors[childIndex % childColors.length];

              const sectionKey = `today-${child.uuid}`;
              const isCollapsed = collapsedSections[sectionKey] || false;

              return (
                <div key={child.uuid} style={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  padding: '12px',
                  border: `2px solid ${childColor}`,
                  backgroundColor: `${childColor}30`
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: isCollapsed ? '8px' : '12px',
                    color: childColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      👤 {child.name}
                    </span>
                    <button
                      onClick={() => toggleSectionCollapse(sectionKey)}
                      style={{
                        background: 'none',
                        border: `1px solid ${childColor}`,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: childColor,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {isCollapsed ? '▼ Expand' : '▲ Collapse'}
                    </button>
                  </h4>
                  {isCollapsed
                    ? activities.map(activity => renderCollapsedActivityCard(activity, child, getActivityColor(activity)))
                    : activities.map(activity => renderActivityCard(activity, child))
                  }
                </div>
              );
            })}
            {/* Parent activities */}
            {Object.entries(todayParentActivities).map(([parentUuid, activities], parentIndex) => {
              if (activities.length === 0) return null;

              // Get parent name from first activity
              const parentName = (activities[0] as any).participant_parent_name || 'Parent';

              // Parent-specific color (different from children colors)
              const parentColor = '#9575cd'; // Purple for parents

              const sectionKey = `today-parent-${parentUuid}`;
              const isCollapsed = collapsedSections[sectionKey] || false;

              return (
                <div key={parentUuid} style={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  padding: '12px',
                  border: `2px solid ${parentColor}`,
                  backgroundColor: `${parentColor}30`
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: isCollapsed ? '8px' : '12px',
                    color: parentColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      👔 {parentName}
                    </span>
                    <button
                      onClick={() => toggleSectionCollapse(sectionKey)}
                      style={{
                        background: 'none',
                        border: `1px solid ${parentColor}`,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: parentColor,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {isCollapsed ? '▼ Expand' : '▲ Collapse'}
                    </button>
                  </h4>
                  {isCollapsed
                    ? activities.map(activity => renderCollapsedActivityCard(activity, null, getActivityColor(activity)))
                    : activities.map(activity => renderActivityCard(activity, null, true))
                  }
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Tomorrow's Activities Section */}
      <div style={{
        marginBottom: hasAnyActivities ? '32px' : '16px',
        padding: '0 12px',
        overflowX: 'hidden'
      }}>
        <h3 style={{
          fontSize: hasAnyActivities ? '20px' : '18px',
          fontWeight: 'bold',
          marginBottom: hasAnyActivities ? '16px' : '8px',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            backgroundColor: '#ff9800',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '14px'
          }}>Tomorrow</span>
        </h3>
        {children.length === 0 && Object.keys(tomorrowParentActivities).length === 0 ? (
          <div style={{ padding: hasAnyActivities ? '16px' : '8px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            No children added yet
          </div>
        ) : Object.values(tomorrowActivities).every(acts => acts.length === 0) && Object.values(tomorrowParentActivities).every(acts => acts.length === 0) ? (
          <div style={{ padding: hasAnyActivities ? '16px' : '8px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            No activities scheduled for tomorrow
          </div>
        ) : (
          <>
            {/* Child activities */}
            {children.map((child, childIndex) => {
              const activities = tomorrowActivities[child.uuid] || [];
              if (activities.length === 0) return null;

              // Muted child colors - index-based to guarantee different colors for siblings
              const childColors = [
                '#5b8cb8', // Muted blue
                '#6a9a6a', // Muted green
                '#b87d7d', // Muted red/rose
                '#c4956a', // Muted orange/tan
                '#8a7ab8', // Muted purple
                '#b87aa8', // Muted pink
                '#5a9a9a', // Muted teal
                '#a8a05a'  // Muted olive/yellow
              ];
              const childColor = childColors[childIndex % childColors.length];

              const sectionKey = `tomorrow-${child.uuid}`;
              const isCollapsed = collapsedSections[sectionKey] || false;

              return (
                <div key={child.uuid} style={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  padding: '12px',
                  border: `2px solid ${childColor}`,
                  backgroundColor: `${childColor}30`
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: isCollapsed ? '8px' : '12px',
                    color: childColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      👤 {child.name}
                    </span>
                    <button
                      onClick={() => toggleSectionCollapse(sectionKey)}
                      style={{
                        background: 'none',
                        border: `1px solid ${childColor}`,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: childColor,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {isCollapsed ? '▼ Expand' : '▲ Collapse'}
                    </button>
                  </h4>
                  {isCollapsed
                    ? activities.map(activity => renderCollapsedActivityCard(activity, child, getActivityColor(activity)))
                    : activities.map(activity => renderActivityCard(activity, child))
                  }
                </div>
              );
            })}
            {/* Parent activities */}
            {Object.entries(tomorrowParentActivities).map(([parentUuid, activities], parentIndex) => {
              if (activities.length === 0) return null;

              // Get parent name from first activity
              const parentName = (activities[0] as any).participant_parent_name || 'Parent';

              // Parent-specific color (different from children colors)
              const parentColor = '#9575cd'; // Purple for parents

              const sectionKey = `tomorrow-parent-${parentUuid}`;
              const isCollapsed = collapsedSections[sectionKey] || false;

              return (
                <div key={parentUuid} style={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  padding: '12px',
                  border: `2px solid ${parentColor}`,
                  backgroundColor: `${parentColor}30`
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: isCollapsed ? '8px' : '12px',
                    color: parentColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      👔 {parentName}
                    </span>
                    <button
                      onClick={() => toggleSectionCollapse(sectionKey)}
                      style={{
                        background: 'none',
                        border: `1px solid ${parentColor}`,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: parentColor,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {isCollapsed ? '▼ Expand' : '▲ Collapse'}
                    </button>
                  </h4>
                  {isCollapsed
                    ? activities.map(activity => renderCollapsedActivityCard(activity, null, getActivityColor(activity)))
                    : activities.map(activity => renderActivityCard(activity, null, true))
                  }
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Holiday Return Modal — flipping back to "at school" is the natural moment
          to ask whether the schedule has changed, because that is when a parent
          learns the new term's timetable. "Same as before" is the existing resume
          path unchanged; "Set up a new term" builds a planner that supersedes the
          current one from today. */}
      {holidayReturnChild && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' }}>
              Welcome back, {holidayReturnChild.name} 👋
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px', lineHeight: 1.5 }}>
              Has the schedule changed for this term?
            </div>

            <button
              onClick={async () => {
                const child = holidayReturnChild;
                setHolidayReturnChild(null);
                if (child) await toggleHolidayMode(child.uuid, child.name);
              }}
              style={{
                width: '100%', padding: '14px', marginBottom: '10px',
                backgroundColor: '#f5f5f5', color: '#1a1a1a', border: '1px solid #ddd',
                borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              Same as before
              <div style={{ fontSize: '12px', fontWeight: '400', color: '#666', marginTop: '2px' }}>
                Resume the existing planner. Nothing changes.
              </div>
            </button>

            <button
              onClick={async () => {
                const child = holidayReturnChild;
                setHolidayReturnChild(null);
                if (!child) return;
                // Clear the pause first so the planner is live again, then hand
                // over to the planner setup flow, which supersedes it from today.
                await toggleHolidayMode(child.uuid, child.name);
                if (onNavigateToTermPlanner) {
                  onNavigateToTermPlanner(child.uuid);
                }
              }}
              style={{
                width: '100%', padding: '14px', marginBottom: '10px',
                backgroundColor: '#4a90d9', color: 'white', border: 'none',
                borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              Set up a new term
              <div style={{ fontSize: '12px', fontWeight: '400', opacity: 0.9, marginTop: '2px' }}>
                Build a new planner. It replaces the current one from today.
              </div>
            </button>

            <button
              onClick={() => setHolidayReturnChild(null)}
              style={{
                width: '100%', padding: '12px', background: 'none', border: 'none',
                color: '#888', fontSize: '14px', cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Expired Term Planner Banner */}
      {expiredTermPlannerChildren.filter(c => !termPlannerBannerDismissed.has(c.childUuid)).length > 0 && (
        <div style={{ marginBottom: '24px', padding: '0 12px' }}>
          {expiredTermPlannerChildren
            .filter(c => !termPlannerBannerDismissed.has(c.childUuid))
            .map(({ childUuid, childName, termName }) => (
            <div key={childUuid} style={{
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              border: '1px solid #ffb74d',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#e65100', fontSize: '15px', marginBottom: '4px' }}>
                  {childName}'s term planner has ended
                </div>
                <div style={{ color: '#bf360c', fontSize: '13px' }}>
                  "{termName}" has finished. Create a new one to keep your schedule up to date.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => {
                    if (onNavigateToTermPlanner) {
                      onNavigateToTermPlanner(childUuid);
                    }
                  }}
                  style={{
                    backgroundColor: '#e65100',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  New Term Planner
                </button>
                <button
                  onClick={() => {
                    const updated = new Set(termPlannerBannerDismissed);
                    updated.add(childUuid);
                    setTermPlannerBannerDismissed(updated);
                    localStorage.setItem('dismissed_term_planner_banners', JSON.stringify(Array.from(updated)));
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#bf360c',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px',
                    lineHeight: 1
                  }}
                  title="Dismiss"
                >
                  x
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notifications Section - EXACT SAME AS ChildrenScreen */}
      <div ref={notificationsSectionRef} style={{ marginBottom: hasAnyActivities ? '32px' : '16px', padding: '0 12px' }}>
        <h3 style={{
          fontSize: hasAnyActivities ? '20px' : '18px',
          fontWeight: 'bold',
          marginBottom: hasAnyActivities ? '16px' : '8px',
          color: '#333',
          borderBottom: '2px solid #1976d2',
          paddingBottom: hasAnyActivities ? '8px' : '6px'
        }}>
          Notifications
        </h3>

        {children.length === 0 ? (
          <div style={{ padding: hasAnyActivities ? '16px' : '8px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            No children added yet
          </div>
        ) : (
          <>
          {repInvitations.map((invitation: any) => (
            <div key={`rep_${invitation.rep_uuid}`} className="invitation-item" onClick={(e) => e.stopPropagation()}>
              <div className="invitation-message">
                {invitation.invited_by_name || 'A class rep'} has asked you to help rep the class{' '}
                <strong>{invitation.class_name}</strong>
                {invitation.school_name ? ` at ${invitation.school_name}` : ''}
                {(invitation.my_children || []).length > 1 && (
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ fontSize: '13px', color: '#555', marginRight: '8px' }}>
                      Which of your children is in {invitation.class_name}?
                    </label>
                    <select
                      value={repChildChoice[invitation.rep_uuid] || ''}
                      onChange={(e) => setRepChildChoice(prev => ({ ...prev, [invitation.rep_uuid]: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ced4da', fontSize: '13px' }}
                    >
                      <option value="">Choose a child</option>
                      {(invitation.my_children || []).map((child: any) => (
                        <option key={child.uuid} value={child.uuid}>{child.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="invitation-actions">
                <button
                  className="invitation-accept-btn"
                  disabled={(invitation.my_children || []).length > 1 && !repChildChoice[invitation.rep_uuid]}
                  onClick={(e) => { e.stopPropagation(); handleRepInvitationResponse(invitation.rep_uuid, 'accept'); }}
                >
                  Accept
                </button>
                <button
                  className="invitation-reject-btn"
                  onClick={(e) => { e.stopPropagation(); handleRepInvitationResponse(invitation.rep_uuid, 'decline'); }}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
          {children.map(child => {
            const invitations = childInvitations[child.uuid] || [];
            const connections = childConnections[child.uuid] || [];
            const systemNotifications = childSystemNotifications[child.uuid] || [];
            const classInvitations = childClassInvitations[child.uuid] || [];
            const totalNotifications = invitations.length + connections.length + systemNotifications.length + classInvitations.length;

            if (totalNotifications === 0) return null;

            return (
              <div key={child.uuid} style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '12px',
                  color: '#555'
                }}>
                  {child.display_name || child.name} ({totalNotifications} notification{totalNotifications !== 1 ? 's' : ''})
                </div>

                {/* Bulk actions — only worth showing once there's more than one */}
                {visibleInvitations(invitations).length > 1 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '8px 12px',
                    marginBottom: '12px',
                    background: '#f5f7fa',
                    border: '1px solid #e1e6ed',
                    borderRadius: '6px'
                  }} onClick={(e) => e.stopPropagation()}>
                    <span style={{ fontSize: '13px', color: '#555', marginRight: 'auto' }}>
                      {visibleInvitations(invitations).length} invitations
                    </span>

                    <button
                      className="invitation-accept-btn"
                      disabled={bulkProcessing === child.uuid}
                      onClick={(e) => { e.stopPropagation(); handleBulkInvitationResponse(child.uuid, 'accept'); }}
                    >
                      {bulkProcessing === child.uuid ? '...' : 'Accept all'}
                    </button>

                    <button
                      className="invitation-reject-btn"
                      disabled={bulkProcessing === child.uuid}
                      onClick={(e) => { e.stopPropagation(); handleBulkInvitationResponse(child.uuid, 'reject'); }}
                    >
                      {bulkProcessing === child.uuid ? '...' : 'Decline all'}
                    </button>

                    <div style={{ position: 'relative' }}>
                      <button
                        className="invitation-dismiss-btn"
                        disabled={bulkProcessing === child.uuid}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBulkSnoozeDropdown(bulkSnoozeDropdown === child.uuid ? null : child.uuid);
                        }}
                      >
                        {bulkProcessing === child.uuid ? '...' : 'Snooze all \u25be'}
                      </button>
                      {bulkSnoozeDropdown === child.uuid && (
                        <div
                          className="dismiss-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '4px',
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: '180px'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ padding: '8px 0' }}>
                            {[
                              { value: '1_day', label: 'Until 1 day before' },
                              { value: '2_days', label: 'Until 2 days before' },
                              { value: '3_days', label: 'Until 3 days before' },
                              { value: '4_days', label: 'Until 4 days before' },
                              { value: '5_days', label: 'Until 5 days before' },
                              { value: '6_days', label: 'Until 6 days before' },
                              { value: '7_days', label: 'Until 7 days before' },
                              { value: '2_weeks', label: 'Until 2 weeks before' }
                            ].map(option => (
                              <div
                                key={option.value}
                                style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBulkSnooze(child.uuid, option.value);
                                }}
                              >
                                {option.label}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Activity Invitations */}
                {visibleInvitations(invitations)
                  .map((invitation) => {
                    const childFullName = invitation.host_child_name || 'Someone';

                    const formatDate = (dateString: string) => {
                      const date = new Date(dateString);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    };

                    // Format time without seconds (HH:MM:SS -> HH:MM)
                    const formatTimeString = (time: string) => {
                      if (!time) return '';
                      const parts = time.split(':');
                      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
                    };

                    const timeRange = invitation.start_time && invitation.end_time
                      ? ` at ${formatTimeString(invitation.start_time)}-${formatTimeString(invitation.end_time)}`
                      : invitation.start_time
                      ? ` at ${formatTimeString(invitation.start_time)}`
                      : '';

                    let displayMessage = '';
                    if ((invitation as any).invitation_message) {
                      displayMessage = (invitation as any).invitation_message;
                    } else if ((invitation as any).isRecurringSeries) {
                      const activityTypeText = invitation.activity_type ? ` (${invitation.activity_type})` : '';
                      displayMessage = invitation.activity_description || `${childFullName} invited you to "${invitation.activity_name}"${activityTypeText}`;
                    } else {
                      const activityTypeText = invitation.activity_type ? ` (${invitation.activity_type})` : '';
                      displayMessage = `${childFullName} invited you to "${invitation.activity_name}"${activityTypeText} on ${formatDate(invitation.start_date)}${timeRange}`;
                    }

                    // Debug logging for tentative status
                    console.log('🔍 HOME: Invitation button render:', {
                      id: invitation.id,
                      invitation_uuid: invitation.invitation_uuid,
                      activity_name: invitation.activity_name,
                      is_tentative: invitation.is_tentative,
                      is_tentative_type: typeof invitation.is_tentative,
                      full_invitation: invitation
                    });

                    return (
                      <div key={invitation.id} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                        <div className="invitation-message" dangerouslySetInnerHTML={{ __html: displayMessage}} />
                        <div className="invitation-actions">
                          <button
                            className="invitation-accept-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const responseId = invitation.invitation_uuid || String(invitation.id);
                              handleInvitationResponse(responseId, 'accept');
                            }}
                            disabled={processingInvitation === invitation.id}
                          >
                            {processingInvitation === invitation.id ? '...' : (invitation.is_tentative ? "I'm Interested" : 'Accept')}
                          </button>
                          <button
                            className="invitation-reject-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const responseId = invitation.invitation_uuid || String(invitation.id);
                              handleInvitationResponse(responseId, 'reject');
                            }}
                            disabled={processingInvitation === invitation.id}
                          >
                            {processingInvitation === invitation.id ? '...' : (invitation.is_tentative ? 'Not Interested' : 'Decline')}
                          </button>
                          <button
                            className="invitation-calendar-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('🔍 HOME: Calendar button clicked for invitation:', {
                                invitation_id: invitation.id,
                                invitation_uuid: invitation.invitation_uuid,
                                activity_name: invitation.activity_name,
                                invitation_start_date: invitation.start_date,
                                invitation_start_date_type: typeof invitation.start_date,
                                child_uuid: child.uuid,
                                child_name: child.name,
                                full_invitation: invitation
                              });
                              onNavigateToChildCalendar?.(child, invitation.start_date);
                              console.log('🔍 HOME: Called onNavigateToChildCalendar with date:', invitation.start_date);
                            }}
                          >
                            Calendar
                          </button>
                          {/* Only show Dismiss button if dismissible is not explicitly false */}
                          {(invitation as any).dismissible !== false && (
                          <div style={{ position: 'relative' }}>
                            <button
                              className="invitation-dismiss-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const invitationUuid = invitation.invitation_uuid || String(invitation.id);
                                setShowDismissDropdown(showDismissDropdown === invitationUuid ? null : invitationUuid);
                              }}
                              disabled={dismissingInvitation === (invitation.invitation_uuid || String(invitation.id))}
                            >
                              {dismissingInvitation === (invitation.invitation_uuid || String(invitation.id)) ? '...' : 'Snooze'}
                            </button>
                            {showDismissDropdown === (invitation.invitation_uuid || String(invitation.id)) && (
                              <div
                                className="dismiss-dropdown"
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: 0,
                                  marginTop: '4px',
                                  backgroundColor: 'white',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                  zIndex: 1000,
                                  minWidth: '150px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div style={{ padding: '8px 0' }}>
                                  {[
                                    { value: '1_day', label: 'Until 1 day before' },
                                    { value: '2_days', label: 'Until 2 days before' },
                                    { value: '3_days', label: 'Until 3 days before' },
                                    { value: '4_days', label: 'Until 4 days before' },
                                    { value: '5_days', label: 'Until 5 days before' },
                                    { value: '6_days', label: 'Until 6 days before' },
                                    { value: '7_days', label: 'Until 7 days before' },
                                    { value: '2_weeks', label: 'Until 2 weeks before' }
                                  ].map(option => (
                                    <div
                                      key={option.value}
                                      style={{
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDismissInvitation((invitation as any).notification_id, option.value);
                                      }}
                                    >
                                      {option.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {/* Class invitations — join the class, not the family */}
                {classInvitations.map((invitation: any) => (
                  <div key={`class_${invitation.member_uuid}`} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                    <div className="invitation-message">
                      {invitation.rep_name || 'A class rep'} has added{' '}
                      {invitation.child_name} to the class <strong>{invitation.class_name}</strong>
                      {invitation.school_name ? ` at ${invitation.school_name}` : ''}
                    </div>
                    <div className="invitation-actions">
                      <button
                        className="invitation-accept-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClassInvitationResponse(invitation.member_uuid, 'accept');
                        }}
                      >
                        Accept
                      </button>
                      <button
                        className="invitation-reject-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClassInvitationResponse(invitation.member_uuid, 'decline');
                        }}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}

                {/* Connection Requests */}
                {connections.map((request) => {
                  return (
                    <div key={`connection_${request.id}`} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                      <div className="invitation-message">
                        {request.class_name ? (
                          <>
                            {request.requester_name || 'A class rep'} has added{' '}
                            {request.target_child_name || 'your child'} to the class{' '}
                            <strong>{request.class_name}</strong> — accept to connect
                          </>
                        ) : (
                          `${request.child_name || 'Someone'} wants to connect`
                        )}
                      </div>
                      <div className="invitation-actions">
                        <button
                          className="invitation-accept-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConnectionResponse(request.request_uuid, 'accept');
                          }}
                          disabled={processingConnection === request.request_uuid}
                        >
                          {processingConnection === request.request_uuid ? '...' : 'Accept'}
                        </button>
                        <button
                          className="invitation-reject-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConnectionResponse(request.request_uuid, 'reject');
                          }}
                          disabled={processingConnection === request.request_uuid}
                        >
                          {processingConnection === request.request_uuid ? '...' : 'Decline'}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* System Notifications & Activity Reminders */}
                {systemNotifications.map((notification) => {
                  const formatDate = (dateString: string) => {
                    const date = new Date(dateString);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                  };

                  const getNotificationIcon = (type: string) => {
                    switch (type) {
                      case 'activity_reminder': return '⏰';
                      case 'connection_rejected': return '❌';
                      case 'connection_accepted': return '✅';
                      case 'activity_rejected': return '❌';
                      case 'activity_accepted': return '✅';
                      case 'guest_decline': return '❌';
                      case 'guest_decline_pending': return '❌';
                      case 'tentative_response': return '👍';
                      case 'activity_confirmed': return '✅';
                      case 'activity_cancelled': return '❌';
                      case 'socs_summary': return '📅';
                      case 'school_portal_import': return '🏫';
                      case 'system':
                      default: return '📢';
                    }
                  };

                  const icon = getNotificationIcon(notification.type);

                  // For tentative_response, show activity date if available
                  const displayDate = (notification.type === 'tentative_response')
                    && (notification.data?.start_date || notification.data?.startDate)
                    ? (notification.data.start_date || notification.data.startDate)
                    : notification.timestamp;

                  // NEVER append dates to these notification types - they already include dates in their messages:
                  // - 'activity_accepted': "Name can now attend "Activity" on Date" or "series (Date - Date)"
                  // - 'guest_decline': "Name can't attend "Activity" on Date" or "series"
                  // - 'guest_decline_pending': Similar to guest_decline
                  // - 'activity_invitation': "Name invited you to Activity on Date" or series info
                  // - 'socs_summary': "The following SOCS fixtures have been automatically added..." (contains all dates)
                  // Only append dates for: connection_*, activity_cancelled, activity_reactivated, activity_updated, host_cant_attend, tentative_response, etc.
                  const typesWithDatesInMessage = ['activity_accepted', 'guest_decline', 'guest_decline_pending', 'activity_invitation', 'socs_summary', 'school_portal_import'];
                  const shouldSkipDate = typesWithDatesInMessage.includes(notification.type);

                  // Build the HTML message with icon and date
                  const iconText = icon;
                  const dateText = !shouldSkipDate ? ` (${formatDate(displayDate)})` : '';
                  // Convert newlines to <br> for multi-line notifications (like socs_summary)
                  const messageWithBreaks = notification.message.replace(/\n/g, '<br>');
                  const fullMessage = `${iconText} ${messageWithBreaks}${dateText}`;

                  return (
                    <div key={notification.id} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                      <div className="invitation-message" dangerouslySetInnerHTML={{ __html: fullMessage}} />

                      <div className="invitation-actions">
                        <button
                          className="invitation-calendar-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            // OPTIMISTIC UPDATE: Immediately remove from UI
                            setChildSystemNotifications(prev => ({
                              ...prev,
                              [child.uuid]: prev[child.uuid].filter(n => n.id !== notification.id)
                            }));
                            // Process API call in background (don't await)
                            apiService.dismissNotification(notification.id, notification.type).catch(error => {
                              console.error('Failed to dismiss notification:', error);
                            });
                          }}
                        >
                          {notification.type === 'connection_accepted' || notification.type === 'connection_rejected' || notification.type === 'activity_cancelled' || notification.type === 'socs_summary' || notification.type === 'school_portal_import' ? 'Dismiss' : 'Mark as Read'}
                        </button>
                        {notification.type !== 'connection_accepted' && notification.type !== 'connection_rejected' && notification.type !== 'activity_cancelled' && notification.type !== 'socs_summary' && notification.type !== 'school_portal_import' && (
                          <button
                            className="invitation-calendar-btn"
                            onClick={(e) => {
                              e.stopPropagation();

                              console.log('🔵 HOME NOTIFICATION CLICK:', {
                                notificationType: notification.type,
                                notificationData: notification.data,
                                childUuid: child.uuid,
                                childName: child.name,
                                hasActivityUuid: !!notification.data?.activity_uuid,
                                activityUuid: notification.data?.activity_uuid
                              });

                              // Set flag to scroll to notifications when returning to home screen
                              sessionStorage.setItem('scrollToNotificationsOnReturn', 'true');

                              // For activity accepted/rejected notifications, navigate to the activity
                              if ((notification.type === 'activity_accepted' || notification.type === 'activity_rejected' ||
                                   notification.type === 'guest_decline' || notification.type === 'guest_decline_pending') &&
                                  notification.data?.activity_uuid) {
                                // Navigate to activity detail
                                const activity = { uuid: notification.data.activity_uuid };
                                console.log('🔵 Calling onNavigateToActivity with:', { child, activity });
                                onNavigateToActivity?.(child, activity);
                              } else {
                                // For invitations and reminders, go to calendar weekly view
                                const activityDate = notification.data?.activityDate || notification.data?.startDate || notification.data?.start_date;
                                console.log('🔵 Calling onNavigateToChildCalendar with:', { child, activityDate });
                                onNavigateToChildCalendar?.(child, activityDate);
                              }
                            }}
                          >
                            {notification.type === 'activity_accepted' || notification.type === 'activity_rejected' ||
                             notification.type === 'guest_decline' || notification.type === 'guest_decline_pending'
                              ? 'View Activity'
                              : 'View Calendar'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          </>
        )}

        {children.length > 0 && repInvitations.length === 0 &&
         Object.values(childInvitations).every(invs => invs.length === 0) &&
         Object.values(childConnections).every(conns => conns.length === 0) &&
         Object.values(childSystemNotifications).every(notifs => notifs.length === 0) && (
          <div style={{ padding: hasAnyActivities ? '16px' : '8px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            No notifications at this time
          </div>
        )}
      </div>

      {/* Designated Parent Selection Modal */}
      {showDesignatedParentModal && (
        <div className="modal-overlay" onClick={() => setShowDesignatedParentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select Designated Parent</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              {pendingBulkAccept
                ? `Who will be responsible for taking the child to these ${pendingBulkAccept.length} activities?`
                : 'Who will be responsible for taking the child to this activity?'}
            </p>

            <select
              className="modal-input"
              value={selectedParentUuid}
              onChange={(e) => setSelectedParentUuid(e.target.value)}
              style={{ marginBottom: '16px' }}
            >
              <option value="">Select a parent...</option>
              {parents.map((parent) => (
                <option key={parent.uuid} value={parent.uuid}>
                  {parent.username} {parent.uuid === user?.uuid ? '(You)' : ''}
                </option>
              ))}
            </select>

            <div className="modal-actions" style={{ display: 'flex', gap: '8px' }}>
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowDesignatedParentModal(false);
                  setProcessingInvitation(null);
                  setPendingBulkAccept(null);
                }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="skip-btn"
                onClick={() => handleDesignatedParentConfirm(true)}
                style={{ flex: 1, background: '#6c757d', color: 'white' }}
              >
                Skip
              </button>
              <button
                className="confirm-btn"
                onClick={() => handleDesignatedParentConfirm(false)}
                disabled={!selectedParentUuid}
                style={{ flex: 1 }}
              >
                {pendingBulkAccept ? `Confirm & Accept All (${pendingBulkAccept.length})` : 'Confirm & Accept'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Configuration Modal (Kit + Parent) */}
      {showConfigureModal && configureActivity && (
        <div className="modal-overlay" onClick={() => !configureSaving && setShowConfigureModal(false)}>
          <div className="modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <h3>⚙️ Configure Activity</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              {configureActivity.name}
            </p>

            {/* Series Prompt */}
            {showSeriesPrompt ? (
              <div>
                <p style={{ fontSize: '14px', marginBottom: '16px', color: '#333' }}>
                  This activity is part of a recurring series. Would you like to apply these changes to all sessions in the series, or just this one?
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowSeriesPrompt(false)}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveConfiguration(false)}
                    disabled={configureSaving}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: configureSaving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {configureSaving ? 'Saving...' : 'Just This Session'}
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={() => handleSaveConfiguration(true)}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    {configureSaving ? 'Saving...' : 'All Sessions'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Uniform Quick-Add Section (for drop-off activities) */}
                {isDropOffActivity(configureActivity) && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                      👔 Uniform & Essentials
                    </label>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      Quick add common items for drop-off:
                    </p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {uniformOptions.map((item) => (
                        <button
                          key={item}
                          onClick={() => {
                            if (!configureKitItems.includes(item)) {
                              setConfigureKitItems([...configureKitItems, item]);
                            }
                          }}
                          disabled={configureKitItems.includes(item)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: configureKitItems.includes(item) ? '#d4edda' : '#f8f9fa',
                            color: configureKitItems.includes(item) ? '#155724' : '#333',
                            border: `1px solid ${configureKitItems.includes(item) ? '#28a745' : '#ddd'}`,
                            borderRadius: '16px',
                            cursor: configureKitItems.includes(item) ? 'default' : 'pointer',
                            fontSize: '12px',
                            opacity: configureKitItems.includes(item) ? 0.7 : 1
                          }}
                        >
                          {configureKitItems.includes(item) ? '✓ ' : '+ '}{item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Kit Items Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                    🎒 {isDropOffActivity(configureActivity) ? 'Additional Items' : 'Kit Items'}
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {configureKitItems.map((item, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: '#e3f2fd',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {item}
                        <button
                          onClick={() => handleRemoveKitItem(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="text"
                      value={configureNewKitItem}
                      onChange={(e) => setConfigureNewKitItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddKitItem()}
                      placeholder="Add kit item..."
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={handleAddKitItem}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Designated Parent Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                    🚗 Designated Parent
                  </label>
                  <select
                    value={configureParentUuid}
                    onChange={(e) => setConfigureParentUuid(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Not assigned</option>
                    {parents.map((parent) => (
                      <option key={parent.uuid} value={parent.uuid}>
                        {parent.username} {parent.uuid === user?.uuid ? '(You)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reminder Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                    Reminder
                  </label>
                  <select
                    value={configureReminderMinutes ?? ''}
                    onChange={(e) => setConfigureReminderMinutes(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">No reminder</option>
                    {REMINDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="modal-actions" style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowConfigureModal(false)}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={handleConfigureSaveClick}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    {configureSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
